/* Supplied premium landing with approved screenshot integration.
 * Screenshot preparation: application/qualification/screens-prepare.cjs.
 * Do not overwrite with the former placeholder-only generator. */
/* eslint-disable */
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useSyncExternalStore, useCallback } from "react";
import { ArrowRight, Menu, X, Check, ShoppingBag, Store, LayoutDashboard, Package, Users, FileText, Calculator, Wallet, BookOpen, Languages, PanelLeft, ChartColumn, PackageCheck, Mail, Plus, ArrowUp } from "lucide-react";
function r(e) {
  var t, f, n = "";
  if ("string" == typeof e || "number" == typeof e) n += e;
  else if ("object" == typeof e) if (Array.isArray(e)) {
    var o = e.length;
    for (t = 0; t < o; t++) e[t] && (f = r(e[t])) && (n && (n += " "), n += f);
  } else for (f in e) e[f] && (n && (n += " "), n += f);
  return n;
}
function clsx() {
  for (var e, t, f = 0, n = "", o = arguments.length; f < o; f++) (e = arguments[f]) && (t = r(e)) && (n && (n += " "), n += t);
  return n;
}
const concatArrays = (array1, array2) => {
  const combinedArray = new Array(array1.length + array2.length);
  for (let i = 0; i < array1.length; i++) {
    combinedArray[i] = array1[i];
  }
  for (let i = 0; i < array2.length; i++) {
    combinedArray[array1.length + i] = array2[i];
  }
  return combinedArray;
};
const createClassValidatorObject = (classGroupId, validator) => ({
  classGroupId,
  validator
});
const createClassPartObject = (nextPart = /* @__PURE__ */ new Map(), validators = null, classGroupId) => ({
  nextPart,
  validators,
  classGroupId
});
const CLASS_PART_SEPARATOR = "-";
const EMPTY_CONFLICTS = [];
const ARBITRARY_PROPERTY_PREFIX = "arbitrary..";
const createClassGroupUtils = (config) => {
  const classMap = createClassMap(config);
  const {
    conflictingClassGroups,
    conflictingClassGroupModifiers
  } = config;
  const getClassGroupId = (className) => {
    if (className.startsWith("[") && className.endsWith("]")) {
      return getGroupIdForArbitraryProperty(className);
    }
    const classParts = className.split(CLASS_PART_SEPARATOR);
    const startIndex = classParts[0] === "" && classParts.length > 1 ? 1 : 0;
    return getGroupRecursive(classParts, startIndex, classMap);
  };
  const getConflictingClassGroupIds = (classGroupId, hasPostfixModifier) => {
    if (hasPostfixModifier) {
      const modifierConflicts = conflictingClassGroupModifiers[classGroupId];
      const baseConflicts = conflictingClassGroups[classGroupId];
      if (modifierConflicts) {
        if (baseConflicts) {
          return concatArrays(baseConflicts, modifierConflicts);
        }
        return modifierConflicts;
      }
      return baseConflicts || EMPTY_CONFLICTS;
    }
    return conflictingClassGroups[classGroupId] || EMPTY_CONFLICTS;
  };
  return {
    getClassGroupId,
    getConflictingClassGroupIds
  };
};
const getGroupRecursive = (classParts, startIndex, classPartObject) => {
  const classPathsLength = classParts.length - startIndex;
  if (classPathsLength === 0) {
    return classPartObject.classGroupId;
  }
  const currentClassPart = classParts[startIndex];
  const nextClassPartObject = classPartObject.nextPart.get(currentClassPart);
  if (nextClassPartObject) {
    const result = getGroupRecursive(classParts, startIndex + 1, nextClassPartObject);
    if (result) return result;
  }
  const validators = classPartObject.validators;
  if (validators === null) {
    return void 0;
  }
  const classRest = startIndex === 0 ? classParts.join(CLASS_PART_SEPARATOR) : classParts.slice(startIndex).join(CLASS_PART_SEPARATOR);
  const validatorsLength = validators.length;
  for (let i = 0; i < validatorsLength; i++) {
    const validatorObj = validators[i];
    if (validatorObj.validator(classRest)) {
      return validatorObj.classGroupId;
    }
  }
  return void 0;
};
const getGroupIdForArbitraryProperty = (className) => className.slice(1, -1).indexOf(":") === -1 ? void 0 : (() => {
  const content = className.slice(1, -1);
  const colonIndex = content.indexOf(":");
  const property = content.slice(0, colonIndex);
  return property ? ARBITRARY_PROPERTY_PREFIX + property : void 0;
})();
const createClassMap = (config) => {
  const {
    theme,
    classGroups
  } = config;
  return processClassGroups(classGroups, theme);
};
const processClassGroups = (classGroups, theme) => {
  const classMap = createClassPartObject();
  for (const classGroupId in classGroups) {
    const group = classGroups[classGroupId];
    processClassesRecursively(group, classMap, classGroupId, theme);
  }
  return classMap;
};
const processClassesRecursively = (classGroup, classPartObject, classGroupId, theme) => {
  const len = classGroup.length;
  for (let i = 0; i < len; i++) {
    const classDefinition = classGroup[i];
    processClassDefinition(classDefinition, classPartObject, classGroupId, theme);
  }
};
const processClassDefinition = (classDefinition, classPartObject, classGroupId, theme) => {
  if (typeof classDefinition === "string") {
    processStringDefinition(classDefinition, classPartObject, classGroupId);
    return;
  }
  if (typeof classDefinition === "function") {
    processFunctionDefinition(classDefinition, classPartObject, classGroupId, theme);
    return;
  }
  processObjectDefinition(classDefinition, classPartObject, classGroupId, theme);
};
const processStringDefinition = (classDefinition, classPartObject, classGroupId) => {
  const classPartObjectToEdit = classDefinition === "" ? classPartObject : getPart(classPartObject, classDefinition);
  classPartObjectToEdit.classGroupId = classGroupId;
};
const processFunctionDefinition = (classDefinition, classPartObject, classGroupId, theme) => {
  if (isThemeGetter(classDefinition)) {
    processClassesRecursively(classDefinition(theme), classPartObject, classGroupId, theme);
    return;
  }
  if (classPartObject.validators === null) {
    classPartObject.validators = [];
  }
  classPartObject.validators.push(createClassValidatorObject(classGroupId, classDefinition));
};
const processObjectDefinition = (classDefinition, classPartObject, classGroupId, theme) => {
  const entries = Object.entries(classDefinition);
  const len = entries.length;
  for (let i = 0; i < len; i++) {
    const [key, value] = entries[i];
    processClassesRecursively(value, getPart(classPartObject, key), classGroupId, theme);
  }
};
const getPart = (classPartObject, path) => {
  let current = classPartObject;
  const parts = path.split(CLASS_PART_SEPARATOR);
  const len = parts.length;
  for (let i = 0; i < len; i++) {
    const part = parts[i];
    let next = current.nextPart.get(part);
    if (!next) {
      next = createClassPartObject();
      current.nextPart.set(part, next);
    }
    current = next;
  }
  return current;
};
const isThemeGetter = (func) => "isThemeGetter" in func && func.isThemeGetter === true;
const createLruCache = (maxCacheSize) => {
  if (maxCacheSize < 1) {
    return {
      get: () => void 0,
      set: () => {
      }
    };
  }
  let cacheSize = 0;
  let cache = /* @__PURE__ */ Object.create(null);
  let previousCache = /* @__PURE__ */ Object.create(null);
  const update = (key, value) => {
    cache[key] = value;
    cacheSize++;
    if (cacheSize > maxCacheSize) {
      cacheSize = 0;
      previousCache = cache;
      cache = /* @__PURE__ */ Object.create(null);
    }
  };
  return {
    get(key) {
      let value = cache[key];
      if (value !== void 0) {
        return value;
      }
      if ((value = previousCache[key]) !== void 0) {
        update(key, value);
        return value;
      }
    },
    set(key, value) {
      if (key in cache) {
        cache[key] = value;
      } else {
        update(key, value);
      }
    }
  };
};
const IMPORTANT_MODIFIER = "!";
const MODIFIER_SEPARATOR = ":";
const EMPTY_MODIFIERS = [];
const createResultObject = (modifiers, hasImportantModifier, baseClassName, maybePostfixModifierPosition, isExternal) => ({
  modifiers,
  hasImportantModifier,
  baseClassName,
  maybePostfixModifierPosition,
  isExternal
});
const createParseClassName = (config) => {
  const {
    prefix,
    experimentalParseClassName
  } = config;
  let parseClassName = (className) => {
    const modifiers = [];
    let bracketDepth = 0;
    let parenDepth = 0;
    let modifierStart = 0;
    let postfixModifierPosition;
    const len = className.length;
    for (let index = 0; index < len; index++) {
      const currentCharacter = className[index];
      if (bracketDepth === 0 && parenDepth === 0) {
        if (currentCharacter === MODIFIER_SEPARATOR) {
          modifiers.push(className.slice(modifierStart, index));
          modifierStart = index + 1;
          continue;
        }
        if (currentCharacter === "/") {
          postfixModifierPosition = index;
          continue;
        }
      }
      if (currentCharacter === "[") bracketDepth++;
      else if (currentCharacter === "]") bracketDepth--;
      else if (currentCharacter === "(") parenDepth++;
      else if (currentCharacter === ")") parenDepth--;
    }
    const baseClassNameWithImportantModifier = modifiers.length === 0 ? className : className.slice(modifierStart);
    let baseClassName = baseClassNameWithImportantModifier;
    let hasImportantModifier = false;
    if (baseClassNameWithImportantModifier.endsWith(IMPORTANT_MODIFIER)) {
      baseClassName = baseClassNameWithImportantModifier.slice(0, -1);
      hasImportantModifier = true;
    } else if (
      /**
       * In Tailwind CSS v3 the important modifier was at the start of the base class name. This is still supported for legacy reasons.
       * @see https://github.com/dcastil/tailwind-merge/issues/513#issuecomment-2614029864
       */
      baseClassNameWithImportantModifier.startsWith(IMPORTANT_MODIFIER)
    ) {
      baseClassName = baseClassNameWithImportantModifier.slice(1);
      hasImportantModifier = true;
    }
    const maybePostfixModifierPosition = postfixModifierPosition && postfixModifierPosition > modifierStart ? postfixModifierPosition - modifierStart : void 0;
    return createResultObject(modifiers, hasImportantModifier, baseClassName, maybePostfixModifierPosition);
  };
  if (prefix) {
    const fullPrefix = prefix + MODIFIER_SEPARATOR;
    const parseClassNameOriginal = parseClassName;
    parseClassName = (className) => className.startsWith(fullPrefix) ? parseClassNameOriginal(className.slice(fullPrefix.length)) : createResultObject(EMPTY_MODIFIERS, false, className, void 0, true);
  }
  if (experimentalParseClassName) {
    const parseClassNameOriginal = parseClassName;
    parseClassName = (className) => experimentalParseClassName({
      className,
      parseClassName: parseClassNameOriginal
    });
  }
  return parseClassName;
};
const createSortModifiers = (config) => {
  const modifierWeights = /* @__PURE__ */ new Map();
  config.orderSensitiveModifiers.forEach((mod, index) => {
    modifierWeights.set(mod, 1e6 + index);
  });
  return (modifiers) => {
    const result = [];
    let currentSegment = [];
    for (let i = 0; i < modifiers.length; i++) {
      const modifier = modifiers[i];
      const isArbitrary = modifier[0] === "[";
      const isOrderSensitive = modifierWeights.has(modifier);
      if (isArbitrary || isOrderSensitive) {
        if (currentSegment.length > 0) {
          currentSegment.sort();
          result.push(...currentSegment);
          currentSegment = [];
        }
        result.push(modifier);
      } else {
        currentSegment.push(modifier);
      }
    }
    if (currentSegment.length > 0) {
      currentSegment.sort();
      result.push(...currentSegment);
    }
    return result;
  };
};
const createConfigUtils = (config) => ({
  cache: createLruCache(config.cacheSize),
  parseClassName: createParseClassName(config),
  sortModifiers: createSortModifiers(config),
  ...createClassGroupUtils(config)
});
const SPLIT_CLASSES_REGEX = /\s+/;
const mergeClassList = (classList, configUtils) => {
  const {
    parseClassName,
    getClassGroupId,
    getConflictingClassGroupIds,
    sortModifiers
  } = configUtils;
  const classGroupsInConflict = [];
  const classNames = classList.trim().split(SPLIT_CLASSES_REGEX);
  let result = "";
  for (let index = classNames.length - 1; index >= 0; index -= 1) {
    const originalClassName = classNames[index];
    const {
      isExternal,
      modifiers,
      hasImportantModifier,
      baseClassName,
      maybePostfixModifierPosition
    } = parseClassName(originalClassName);
    if (isExternal) {
      result = originalClassName + (result.length > 0 ? " " + result : result);
      continue;
    }
    let hasPostfixModifier = !!maybePostfixModifierPosition;
    let classGroupId = getClassGroupId(hasPostfixModifier ? baseClassName.substring(0, maybePostfixModifierPosition) : baseClassName);
    if (!classGroupId) {
      if (!hasPostfixModifier) {
        result = originalClassName + (result.length > 0 ? " " + result : result);
        continue;
      }
      classGroupId = getClassGroupId(baseClassName);
      if (!classGroupId) {
        result = originalClassName + (result.length > 0 ? " " + result : result);
        continue;
      }
      hasPostfixModifier = false;
    }
    const variantModifier = modifiers.length === 0 ? "" : modifiers.length === 1 ? modifiers[0] : sortModifiers(modifiers).join(":");
    const modifierId = hasImportantModifier ? variantModifier + IMPORTANT_MODIFIER : variantModifier;
    const classId = modifierId + classGroupId;
    if (classGroupsInConflict.indexOf(classId) > -1) {
      continue;
    }
    classGroupsInConflict.push(classId);
    const conflictGroups = getConflictingClassGroupIds(classGroupId, hasPostfixModifier);
    for (let i = 0; i < conflictGroups.length; ++i) {
      const group = conflictGroups[i];
      classGroupsInConflict.push(modifierId + group);
    }
    result = originalClassName + (result.length > 0 ? " " + result : result);
  }
  return result;
};
const twJoin = (...classLists) => {
  let index = 0;
  let argument;
  let resolvedValue;
  let string = "";
  while (index < classLists.length) {
    if (argument = classLists[index++]) {
      if (resolvedValue = toValue(argument)) {
        string && (string += " ");
        string += resolvedValue;
      }
    }
  }
  return string;
};
const toValue = (mix) => {
  if (typeof mix === "string") {
    return mix;
  }
  let resolvedValue;
  let string = "";
  for (let k = 0; k < mix.length; k++) {
    if (mix[k]) {
      if (resolvedValue = toValue(mix[k])) {
        string && (string += " ");
        string += resolvedValue;
      }
    }
  }
  return string;
};
const createTailwindMerge = (createConfigFirst, ...createConfigRest) => {
  let configUtils;
  let cacheGet;
  let cacheSet;
  let functionToCall;
  const initTailwindMerge = (classList) => {
    const config = createConfigRest.reduce((previousConfig, createConfigCurrent) => createConfigCurrent(previousConfig), createConfigFirst());
    configUtils = createConfigUtils(config);
    cacheGet = configUtils.cache.get;
    cacheSet = configUtils.cache.set;
    functionToCall = tailwindMerge;
    return tailwindMerge(classList);
  };
  const tailwindMerge = (classList) => {
    const cachedResult = cacheGet(classList);
    if (cachedResult) {
      return cachedResult;
    }
    const result = mergeClassList(classList, configUtils);
    cacheSet(classList, result);
    return result;
  };
  functionToCall = initTailwindMerge;
  return (...args) => functionToCall(twJoin(...args));
};
const fallbackThemeArr = [];
const fromTheme = (key) => {
  const themeGetter = (theme) => theme[key] || fallbackThemeArr;
  themeGetter.isThemeGetter = true;
  return themeGetter;
};
const arbitraryValueRegex = /^\[(?:(\w[\w-]*):)?(.+)\]$/i;
const arbitraryVariableRegex = /^\((?:(\w[\w-]*):)?(.+)\)$/i;
const fractionRegex = /^\d+\/\d+$/;
const tshirtUnitRegex = /^(\d+(\.\d+)?)?(xs|sm|md|lg|xl)$/;
const lengthUnitRegex = /\d+(%|px|r?em|[sdl]?v([hwib]|min|max)|pt|pc|in|cm|mm|cap|ch|ex|r?lh|cq(w|h|i|b|min|max))|\b(calc|min|max|clamp)\(.+\)|^0$/;
const colorFunctionRegex = /^(rgba?|hsla?|hwb|(ok)?(lab|lch)|color-mix)\(.+\)$/;
const shadowRegex = /^(inset_)?-?((\d+)?\.?(\d+)[a-z]+|0)_-?((\d+)?\.?(\d+)[a-z]+|0)/;
const imageRegex = /^(url|image|image-set|cross-fade|element|(repeating-)?(linear|radial|conic)-gradient)\(.+\)$/;
const isFraction = (value) => fractionRegex.test(value);
const isNumber = (value) => !!value && !Number.isNaN(Number(value));
const isInteger = (value) => !!value && Number.isInteger(Number(value));
const isPercent = (value) => value.endsWith("%") && isNumber(value.slice(0, -1));
const isTshirtSize = (value) => tshirtUnitRegex.test(value);
const isAny = () => true;
const isLengthOnly = (value) => (
  // `colorFunctionRegex` check is necessary because color functions can have percentages in them which which would be incorrectly classified as lengths.
  // For example, `hsl(0 0% 0%)` would be classified as a length without this check.
  // I could also use lookbehind assertion in `lengthUnitRegex` but that isn't supported widely enough.
  lengthUnitRegex.test(value) && !colorFunctionRegex.test(value)
);
const isNever = () => false;
const isShadow = (value) => shadowRegex.test(value);
const isImage = (value) => imageRegex.test(value);
const isAnyNonArbitrary = (value) => !isArbitraryValue(value) && !isArbitraryVariable(value);
const isArbitrarySize = (value) => getIsArbitraryValue(value, isLabelSize, isNever);
const isArbitraryValue = (value) => arbitraryValueRegex.test(value);
const isArbitraryLength = (value) => getIsArbitraryValue(value, isLabelLength, isLengthOnly);
const isArbitraryNumber = (value) => getIsArbitraryValue(value, isLabelNumber, isNumber);
const isArbitraryPosition = (value) => getIsArbitraryValue(value, isLabelPosition, isNever);
const isArbitraryImage = (value) => getIsArbitraryValue(value, isLabelImage, isImage);
const isArbitraryShadow = (value) => getIsArbitraryValue(value, isLabelShadow, isShadow);
const isArbitraryVariable = (value) => arbitraryVariableRegex.test(value);
const isArbitraryVariableLength = (value) => getIsArbitraryVariable(value, isLabelLength);
const isArbitraryVariableFamilyName = (value) => getIsArbitraryVariable(value, isLabelFamilyName);
const isArbitraryVariablePosition = (value) => getIsArbitraryVariable(value, isLabelPosition);
const isArbitraryVariableSize = (value) => getIsArbitraryVariable(value, isLabelSize);
const isArbitraryVariableImage = (value) => getIsArbitraryVariable(value, isLabelImage);
const isArbitraryVariableShadow = (value) => getIsArbitraryVariable(value, isLabelShadow, true);
const getIsArbitraryValue = (value, testLabel, testValue) => {
  const result = arbitraryValueRegex.exec(value);
  if (result) {
    if (result[1]) {
      return testLabel(result[1]);
    }
    return testValue(result[2]);
  }
  return false;
};
const getIsArbitraryVariable = (value, testLabel, shouldMatchNoLabel = false) => {
  const result = arbitraryVariableRegex.exec(value);
  if (result) {
    if (result[1]) {
      return testLabel(result[1]);
    }
    return shouldMatchNoLabel;
  }
  return false;
};
const isLabelPosition = (label) => label === "position" || label === "percentage";
const isLabelImage = (label) => label === "image" || label === "url";
const isLabelSize = (label) => label === "length" || label === "size" || label === "bg-size";
const isLabelLength = (label) => label === "length";
const isLabelNumber = (label) => label === "number";
const isLabelFamilyName = (label) => label === "family-name";
const isLabelShadow = (label) => label === "shadow";
const getDefaultConfig = () => {
  const themeColor = fromTheme("color");
  const themeFont = fromTheme("font");
  const themeText = fromTheme("text");
  const themeFontWeight = fromTheme("font-weight");
  const themeTracking = fromTheme("tracking");
  const themeLeading = fromTheme("leading");
  const themeBreakpoint = fromTheme("breakpoint");
  const themeContainer = fromTheme("container");
  const themeSpacing = fromTheme("spacing");
  const themeRadius = fromTheme("radius");
  const themeShadow = fromTheme("shadow");
  const themeInsetShadow = fromTheme("inset-shadow");
  const themeTextShadow = fromTheme("text-shadow");
  const themeDropShadow = fromTheme("drop-shadow");
  const themeBlur = fromTheme("blur");
  const themePerspective = fromTheme("perspective");
  const themeAspect = fromTheme("aspect");
  const themeEase = fromTheme("ease");
  const themeAnimate = fromTheme("animate");
  const scaleBreak = () => ["auto", "avoid", "all", "avoid-page", "page", "left", "right", "column"];
  const scalePosition = () => [
    "center",
    "top",
    "bottom",
    "left",
    "right",
    "top-left",
    // Deprecated since Tailwind CSS v4.1.0, see https://github.com/tailwindlabs/tailwindcss/pull/17378
    "left-top",
    "top-right",
    // Deprecated since Tailwind CSS v4.1.0, see https://github.com/tailwindlabs/tailwindcss/pull/17378
    "right-top",
    "bottom-right",
    // Deprecated since Tailwind CSS v4.1.0, see https://github.com/tailwindlabs/tailwindcss/pull/17378
    "right-bottom",
    "bottom-left",
    // Deprecated since Tailwind CSS v4.1.0, see https://github.com/tailwindlabs/tailwindcss/pull/17378
    "left-bottom"
  ];
  const scalePositionWithArbitrary = () => [...scalePosition(), isArbitraryVariable, isArbitraryValue];
  const scaleOverflow = () => ["auto", "hidden", "clip", "visible", "scroll"];
  const scaleOverscroll = () => ["auto", "contain", "none"];
  const scaleUnambiguousSpacing = () => [isArbitraryVariable, isArbitraryValue, themeSpacing];
  const scaleInset = () => [isFraction, "full", "auto", ...scaleUnambiguousSpacing()];
  const scaleGridTemplateColsRows = () => [isInteger, "none", "subgrid", isArbitraryVariable, isArbitraryValue];
  const scaleGridColRowStartAndEnd = () => ["auto", {
    span: ["full", isInteger, isArbitraryVariable, isArbitraryValue]
  }, isInteger, isArbitraryVariable, isArbitraryValue];
  const scaleGridColRowStartOrEnd = () => [isInteger, "auto", isArbitraryVariable, isArbitraryValue];
  const scaleGridAutoColsRows = () => ["auto", "min", "max", "fr", isArbitraryVariable, isArbitraryValue];
  const scaleAlignPrimaryAxis = () => ["start", "end", "center", "between", "around", "evenly", "stretch", "baseline", "center-safe", "end-safe"];
  const scaleAlignSecondaryAxis = () => ["start", "end", "center", "stretch", "center-safe", "end-safe"];
  const scaleMargin = () => ["auto", ...scaleUnambiguousSpacing()];
  const scaleSizing = () => [isFraction, "auto", "full", "dvw", "dvh", "lvw", "lvh", "svw", "svh", "min", "max", "fit", ...scaleUnambiguousSpacing()];
  const scaleColor = () => [themeColor, isArbitraryVariable, isArbitraryValue];
  const scaleBgPosition = () => [...scalePosition(), isArbitraryVariablePosition, isArbitraryPosition, {
    position: [isArbitraryVariable, isArbitraryValue]
  }];
  const scaleBgRepeat = () => ["no-repeat", {
    repeat: ["", "x", "y", "space", "round"]
  }];
  const scaleBgSize = () => ["auto", "cover", "contain", isArbitraryVariableSize, isArbitrarySize, {
    size: [isArbitraryVariable, isArbitraryValue]
  }];
  const scaleGradientStopPosition = () => [isPercent, isArbitraryVariableLength, isArbitraryLength];
  const scaleRadius = () => [
    // Deprecated since Tailwind CSS v4.0.0
    "",
    "none",
    "full",
    themeRadius,
    isArbitraryVariable,
    isArbitraryValue
  ];
  const scaleBorderWidth = () => ["", isNumber, isArbitraryVariableLength, isArbitraryLength];
  const scaleLineStyle = () => ["solid", "dashed", "dotted", "double"];
  const scaleBlendMode = () => ["normal", "multiply", "screen", "overlay", "darken", "lighten", "color-dodge", "color-burn", "hard-light", "soft-light", "difference", "exclusion", "hue", "saturation", "color", "luminosity"];
  const scaleMaskImagePosition = () => [isNumber, isPercent, isArbitraryVariablePosition, isArbitraryPosition];
  const scaleBlur = () => [
    // Deprecated since Tailwind CSS v4.0.0
    "",
    "none",
    themeBlur,
    isArbitraryVariable,
    isArbitraryValue
  ];
  const scaleRotate = () => ["none", isNumber, isArbitraryVariable, isArbitraryValue];
  const scaleScale = () => ["none", isNumber, isArbitraryVariable, isArbitraryValue];
  const scaleSkew = () => [isNumber, isArbitraryVariable, isArbitraryValue];
  const scaleTranslate = () => [isFraction, "full", ...scaleUnambiguousSpacing()];
  return {
    cacheSize: 500,
    theme: {
      animate: ["spin", "ping", "pulse", "bounce"],
      aspect: ["video"],
      blur: [isTshirtSize],
      breakpoint: [isTshirtSize],
      color: [isAny],
      container: [isTshirtSize],
      "drop-shadow": [isTshirtSize],
      ease: ["in", "out", "in-out"],
      font: [isAnyNonArbitrary],
      "font-weight": ["thin", "extralight", "light", "normal", "medium", "semibold", "bold", "extrabold", "black"],
      "inset-shadow": [isTshirtSize],
      leading: ["none", "tight", "snug", "normal", "relaxed", "loose"],
      perspective: ["dramatic", "near", "normal", "midrange", "distant", "none"],
      radius: [isTshirtSize],
      shadow: [isTshirtSize],
      spacing: ["px", isNumber],
      text: [isTshirtSize],
      "text-shadow": [isTshirtSize],
      tracking: ["tighter", "tight", "normal", "wide", "wider", "widest"]
    },
    classGroups: {
      // --------------
      // --- Layout ---
      // --------------
      /**
       * Aspect Ratio
       * @see https://tailwindcss.com/docs/aspect-ratio
       */
      aspect: [{
        aspect: ["auto", "square", isFraction, isArbitraryValue, isArbitraryVariable, themeAspect]
      }],
      /**
       * Container
       * @see https://tailwindcss.com/docs/container
       * @deprecated since Tailwind CSS v4.0.0
       */
      container: ["container"],
      /**
       * Columns
       * @see https://tailwindcss.com/docs/columns
       */
      columns: [{
        columns: [isNumber, isArbitraryValue, isArbitraryVariable, themeContainer]
      }],
      /**
       * Break After
       * @see https://tailwindcss.com/docs/break-after
       */
      "break-after": [{
        "break-after": scaleBreak()
      }],
      /**
       * Break Before
       * @see https://tailwindcss.com/docs/break-before
       */
      "break-before": [{
        "break-before": scaleBreak()
      }],
      /**
       * Break Inside
       * @see https://tailwindcss.com/docs/break-inside
       */
      "break-inside": [{
        "break-inside": ["auto", "avoid", "avoid-page", "avoid-column"]
      }],
      /**
       * Box Decoration Break
       * @see https://tailwindcss.com/docs/box-decoration-break
       */
      "box-decoration": [{
        "box-decoration": ["slice", "clone"]
      }],
      /**
       * Box Sizing
       * @see https://tailwindcss.com/docs/box-sizing
       */
      box: [{
        box: ["border", "content"]
      }],
      /**
       * Display
       * @see https://tailwindcss.com/docs/display
       */
      display: ["block", "inline-block", "inline", "flex", "inline-flex", "table", "inline-table", "table-caption", "table-cell", "table-column", "table-column-group", "table-footer-group", "table-header-group", "table-row-group", "table-row", "flow-root", "grid", "inline-grid", "contents", "list-item", "hidden"],
      /**
       * Screen Reader Only
       * @see https://tailwindcss.com/docs/display#screen-reader-only
       */
      sr: ["sr-only", "not-sr-only"],
      /**
       * Floats
       * @see https://tailwindcss.com/docs/float
       */
      float: [{
        float: ["right", "left", "none", "start", "end"]
      }],
      /**
       * Clear
       * @see https://tailwindcss.com/docs/clear
       */
      clear: [{
        clear: ["left", "right", "both", "none", "start", "end"]
      }],
      /**
       * Isolation
       * @see https://tailwindcss.com/docs/isolation
       */
      isolation: ["isolate", "isolation-auto"],
      /**
       * Object Fit
       * @see https://tailwindcss.com/docs/object-fit
       */
      "object-fit": [{
        object: ["contain", "cover", "fill", "none", "scale-down"]
      }],
      /**
       * Object Position
       * @see https://tailwindcss.com/docs/object-position
       */
      "object-position": [{
        object: scalePositionWithArbitrary()
      }],
      /**
       * Overflow
       * @see https://tailwindcss.com/docs/overflow
       */
      overflow: [{
        overflow: scaleOverflow()
      }],
      /**
       * Overflow X
       * @see https://tailwindcss.com/docs/overflow
       */
      "overflow-x": [{
        "overflow-x": scaleOverflow()
      }],
      /**
       * Overflow Y
       * @see https://tailwindcss.com/docs/overflow
       */
      "overflow-y": [{
        "overflow-y": scaleOverflow()
      }],
      /**
       * Overscroll Behavior
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      overscroll: [{
        overscroll: scaleOverscroll()
      }],
      /**
       * Overscroll Behavior X
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      "overscroll-x": [{
        "overscroll-x": scaleOverscroll()
      }],
      /**
       * Overscroll Behavior Y
       * @see https://tailwindcss.com/docs/overscroll-behavior
       */
      "overscroll-y": [{
        "overscroll-y": scaleOverscroll()
      }],
      /**
       * Position
       * @see https://tailwindcss.com/docs/position
       */
      position: ["static", "fixed", "absolute", "relative", "sticky"],
      /**
       * Top / Right / Bottom / Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      inset: [{
        inset: scaleInset()
      }],
      /**
       * Right / Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      "inset-x": [{
        "inset-x": scaleInset()
      }],
      /**
       * Top / Bottom
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      "inset-y": [{
        "inset-y": scaleInset()
      }],
      /**
       * Start
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      start: [{
        start: scaleInset()
      }],
      /**
       * End
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      end: [{
        end: scaleInset()
      }],
      /**
       * Top
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      top: [{
        top: scaleInset()
      }],
      /**
       * Right
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      right: [{
        right: scaleInset()
      }],
      /**
       * Bottom
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      bottom: [{
        bottom: scaleInset()
      }],
      /**
       * Left
       * @see https://tailwindcss.com/docs/top-right-bottom-left
       */
      left: [{
        left: scaleInset()
      }],
      /**
       * Visibility
       * @see https://tailwindcss.com/docs/visibility
       */
      visibility: ["visible", "invisible", "collapse"],
      /**
       * Z-Index
       * @see https://tailwindcss.com/docs/z-index
       */
      z: [{
        z: [isInteger, "auto", isArbitraryVariable, isArbitraryValue]
      }],
      // ------------------------
      // --- Flexbox and Grid ---
      // ------------------------
      /**
       * Flex Basis
       * @see https://tailwindcss.com/docs/flex-basis
       */
      basis: [{
        basis: [isFraction, "full", "auto", themeContainer, ...scaleUnambiguousSpacing()]
      }],
      /**
       * Flex Direction
       * @see https://tailwindcss.com/docs/flex-direction
       */
      "flex-direction": [{
        flex: ["row", "row-reverse", "col", "col-reverse"]
      }],
      /**
       * Flex Wrap
       * @see https://tailwindcss.com/docs/flex-wrap
       */
      "flex-wrap": [{
        flex: ["nowrap", "wrap", "wrap-reverse"]
      }],
      /**
       * Flex
       * @see https://tailwindcss.com/docs/flex
       */
      flex: [{
        flex: [isNumber, isFraction, "auto", "initial", "none", isArbitraryValue]
      }],
      /**
       * Flex Grow
       * @see https://tailwindcss.com/docs/flex-grow
       */
      grow: [{
        grow: ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Flex Shrink
       * @see https://tailwindcss.com/docs/flex-shrink
       */
      shrink: [{
        shrink: ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Order
       * @see https://tailwindcss.com/docs/order
       */
      order: [{
        order: [isInteger, "first", "last", "none", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Grid Template Columns
       * @see https://tailwindcss.com/docs/grid-template-columns
       */
      "grid-cols": [{
        "grid-cols": scaleGridTemplateColsRows()
      }],
      /**
       * Grid Column Start / End
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-start-end": [{
        col: scaleGridColRowStartAndEnd()
      }],
      /**
       * Grid Column Start
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-start": [{
        "col-start": scaleGridColRowStartOrEnd()
      }],
      /**
       * Grid Column End
       * @see https://tailwindcss.com/docs/grid-column
       */
      "col-end": [{
        "col-end": scaleGridColRowStartOrEnd()
      }],
      /**
       * Grid Template Rows
       * @see https://tailwindcss.com/docs/grid-template-rows
       */
      "grid-rows": [{
        "grid-rows": scaleGridTemplateColsRows()
      }],
      /**
       * Grid Row Start / End
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-start-end": [{
        row: scaleGridColRowStartAndEnd()
      }],
      /**
       * Grid Row Start
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-start": [{
        "row-start": scaleGridColRowStartOrEnd()
      }],
      /**
       * Grid Row End
       * @see https://tailwindcss.com/docs/grid-row
       */
      "row-end": [{
        "row-end": scaleGridColRowStartOrEnd()
      }],
      /**
       * Grid Auto Flow
       * @see https://tailwindcss.com/docs/grid-auto-flow
       */
      "grid-flow": [{
        "grid-flow": ["row", "col", "dense", "row-dense", "col-dense"]
      }],
      /**
       * Grid Auto Columns
       * @see https://tailwindcss.com/docs/grid-auto-columns
       */
      "auto-cols": [{
        "auto-cols": scaleGridAutoColsRows()
      }],
      /**
       * Grid Auto Rows
       * @see https://tailwindcss.com/docs/grid-auto-rows
       */
      "auto-rows": [{
        "auto-rows": scaleGridAutoColsRows()
      }],
      /**
       * Gap
       * @see https://tailwindcss.com/docs/gap
       */
      gap: [{
        gap: scaleUnambiguousSpacing()
      }],
      /**
       * Gap X
       * @see https://tailwindcss.com/docs/gap
       */
      "gap-x": [{
        "gap-x": scaleUnambiguousSpacing()
      }],
      /**
       * Gap Y
       * @see https://tailwindcss.com/docs/gap
       */
      "gap-y": [{
        "gap-y": scaleUnambiguousSpacing()
      }],
      /**
       * Justify Content
       * @see https://tailwindcss.com/docs/justify-content
       */
      "justify-content": [{
        justify: [...scaleAlignPrimaryAxis(), "normal"]
      }],
      /**
       * Justify Items
       * @see https://tailwindcss.com/docs/justify-items
       */
      "justify-items": [{
        "justify-items": [...scaleAlignSecondaryAxis(), "normal"]
      }],
      /**
       * Justify Self
       * @see https://tailwindcss.com/docs/justify-self
       */
      "justify-self": [{
        "justify-self": ["auto", ...scaleAlignSecondaryAxis()]
      }],
      /**
       * Align Content
       * @see https://tailwindcss.com/docs/align-content
       */
      "align-content": [{
        content: ["normal", ...scaleAlignPrimaryAxis()]
      }],
      /**
       * Align Items
       * @see https://tailwindcss.com/docs/align-items
       */
      "align-items": [{
        items: [...scaleAlignSecondaryAxis(), {
          baseline: ["", "last"]
        }]
      }],
      /**
       * Align Self
       * @see https://tailwindcss.com/docs/align-self
       */
      "align-self": [{
        self: ["auto", ...scaleAlignSecondaryAxis(), {
          baseline: ["", "last"]
        }]
      }],
      /**
       * Place Content
       * @see https://tailwindcss.com/docs/place-content
       */
      "place-content": [{
        "place-content": scaleAlignPrimaryAxis()
      }],
      /**
       * Place Items
       * @see https://tailwindcss.com/docs/place-items
       */
      "place-items": [{
        "place-items": [...scaleAlignSecondaryAxis(), "baseline"]
      }],
      /**
       * Place Self
       * @see https://tailwindcss.com/docs/place-self
       */
      "place-self": [{
        "place-self": ["auto", ...scaleAlignSecondaryAxis()]
      }],
      // Spacing
      /**
       * Padding
       * @see https://tailwindcss.com/docs/padding
       */
      p: [{
        p: scaleUnambiguousSpacing()
      }],
      /**
       * Padding X
       * @see https://tailwindcss.com/docs/padding
       */
      px: [{
        px: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Y
       * @see https://tailwindcss.com/docs/padding
       */
      py: [{
        py: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Start
       * @see https://tailwindcss.com/docs/padding
       */
      ps: [{
        ps: scaleUnambiguousSpacing()
      }],
      /**
       * Padding End
       * @see https://tailwindcss.com/docs/padding
       */
      pe: [{
        pe: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Top
       * @see https://tailwindcss.com/docs/padding
       */
      pt: [{
        pt: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Right
       * @see https://tailwindcss.com/docs/padding
       */
      pr: [{
        pr: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Bottom
       * @see https://tailwindcss.com/docs/padding
       */
      pb: [{
        pb: scaleUnambiguousSpacing()
      }],
      /**
       * Padding Left
       * @see https://tailwindcss.com/docs/padding
       */
      pl: [{
        pl: scaleUnambiguousSpacing()
      }],
      /**
       * Margin
       * @see https://tailwindcss.com/docs/margin
       */
      m: [{
        m: scaleMargin()
      }],
      /**
       * Margin X
       * @see https://tailwindcss.com/docs/margin
       */
      mx: [{
        mx: scaleMargin()
      }],
      /**
       * Margin Y
       * @see https://tailwindcss.com/docs/margin
       */
      my: [{
        my: scaleMargin()
      }],
      /**
       * Margin Start
       * @see https://tailwindcss.com/docs/margin
       */
      ms: [{
        ms: scaleMargin()
      }],
      /**
       * Margin End
       * @see https://tailwindcss.com/docs/margin
       */
      me: [{
        me: scaleMargin()
      }],
      /**
       * Margin Top
       * @see https://tailwindcss.com/docs/margin
       */
      mt: [{
        mt: scaleMargin()
      }],
      /**
       * Margin Right
       * @see https://tailwindcss.com/docs/margin
       */
      mr: [{
        mr: scaleMargin()
      }],
      /**
       * Margin Bottom
       * @see https://tailwindcss.com/docs/margin
       */
      mb: [{
        mb: scaleMargin()
      }],
      /**
       * Margin Left
       * @see https://tailwindcss.com/docs/margin
       */
      ml: [{
        ml: scaleMargin()
      }],
      /**
       * Space Between X
       * @see https://tailwindcss.com/docs/margin#adding-space-between-children
       */
      "space-x": [{
        "space-x": scaleUnambiguousSpacing()
      }],
      /**
       * Space Between X Reverse
       * @see https://tailwindcss.com/docs/margin#adding-space-between-children
       */
      "space-x-reverse": ["space-x-reverse"],
      /**
       * Space Between Y
       * @see https://tailwindcss.com/docs/margin#adding-space-between-children
       */
      "space-y": [{
        "space-y": scaleUnambiguousSpacing()
      }],
      /**
       * Space Between Y Reverse
       * @see https://tailwindcss.com/docs/margin#adding-space-between-children
       */
      "space-y-reverse": ["space-y-reverse"],
      // --------------
      // --- Sizing ---
      // --------------
      /**
       * Size
       * @see https://tailwindcss.com/docs/width#setting-both-width-and-height
       */
      size: [{
        size: scaleSizing()
      }],
      /**
       * Width
       * @see https://tailwindcss.com/docs/width
       */
      w: [{
        w: [themeContainer, "screen", ...scaleSizing()]
      }],
      /**
       * Min-Width
       * @see https://tailwindcss.com/docs/min-width
       */
      "min-w": [{
        "min-w": [
          themeContainer,
          "screen",
          /** Deprecated. @see https://github.com/tailwindlabs/tailwindcss.com/issues/2027#issuecomment-2620152757 */
          "none",
          ...scaleSizing()
        ]
      }],
      /**
       * Max-Width
       * @see https://tailwindcss.com/docs/max-width
       */
      "max-w": [{
        "max-w": [
          themeContainer,
          "screen",
          "none",
          /** Deprecated since Tailwind CSS v4.0.0. @see https://github.com/tailwindlabs/tailwindcss.com/issues/2027#issuecomment-2620152757 */
          "prose",
          /** Deprecated since Tailwind CSS v4.0.0. @see https://github.com/tailwindlabs/tailwindcss.com/issues/2027#issuecomment-2620152757 */
          {
            screen: [themeBreakpoint]
          },
          ...scaleSizing()
        ]
      }],
      /**
       * Height
       * @see https://tailwindcss.com/docs/height
       */
      h: [{
        h: ["screen", "lh", ...scaleSizing()]
      }],
      /**
       * Min-Height
       * @see https://tailwindcss.com/docs/min-height
       */
      "min-h": [{
        "min-h": ["screen", "lh", "none", ...scaleSizing()]
      }],
      /**
       * Max-Height
       * @see https://tailwindcss.com/docs/max-height
       */
      "max-h": [{
        "max-h": ["screen", "lh", ...scaleSizing()]
      }],
      // ------------------
      // --- Typography ---
      // ------------------
      /**
       * Font Size
       * @see https://tailwindcss.com/docs/font-size
       */
      "font-size": [{
        text: ["base", themeText, isArbitraryVariableLength, isArbitraryLength]
      }],
      /**
       * Font Smoothing
       * @see https://tailwindcss.com/docs/font-smoothing
       */
      "font-smoothing": ["antialiased", "subpixel-antialiased"],
      /**
       * Font Style
       * @see https://tailwindcss.com/docs/font-style
       */
      "font-style": ["italic", "not-italic"],
      /**
       * Font Weight
       * @see https://tailwindcss.com/docs/font-weight
       */
      "font-weight": [{
        font: [themeFontWeight, isArbitraryVariable, isArbitraryNumber]
      }],
      /**
       * Font Stretch
       * @see https://tailwindcss.com/docs/font-stretch
       */
      "font-stretch": [{
        "font-stretch": ["ultra-condensed", "extra-condensed", "condensed", "semi-condensed", "normal", "semi-expanded", "expanded", "extra-expanded", "ultra-expanded", isPercent, isArbitraryValue]
      }],
      /**
       * Font Family
       * @see https://tailwindcss.com/docs/font-family
       */
      "font-family": [{
        font: [isArbitraryVariableFamilyName, isArbitraryValue, themeFont]
      }],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-normal": ["normal-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-ordinal": ["ordinal"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-slashed-zero": ["slashed-zero"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-figure": ["lining-nums", "oldstyle-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-spacing": ["proportional-nums", "tabular-nums"],
      /**
       * Font Variant Numeric
       * @see https://tailwindcss.com/docs/font-variant-numeric
       */
      "fvn-fraction": ["diagonal-fractions", "stacked-fractions"],
      /**
       * Letter Spacing
       * @see https://tailwindcss.com/docs/letter-spacing
       */
      tracking: [{
        tracking: [themeTracking, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Line Clamp
       * @see https://tailwindcss.com/docs/line-clamp
       */
      "line-clamp": [{
        "line-clamp": [isNumber, "none", isArbitraryVariable, isArbitraryNumber]
      }],
      /**
       * Line Height
       * @see https://tailwindcss.com/docs/line-height
       */
      leading: [{
        leading: [
          /** Deprecated since Tailwind CSS v4.0.0. @see https://github.com/tailwindlabs/tailwindcss.com/issues/2027#issuecomment-2620152757 */
          themeLeading,
          ...scaleUnambiguousSpacing()
        ]
      }],
      /**
       * List Style Image
       * @see https://tailwindcss.com/docs/list-style-image
       */
      "list-image": [{
        "list-image": ["none", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * List Style Position
       * @see https://tailwindcss.com/docs/list-style-position
       */
      "list-style-position": [{
        list: ["inside", "outside"]
      }],
      /**
       * List Style Type
       * @see https://tailwindcss.com/docs/list-style-type
       */
      "list-style-type": [{
        list: ["disc", "decimal", "none", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Text Alignment
       * @see https://tailwindcss.com/docs/text-align
       */
      "text-alignment": [{
        text: ["left", "center", "right", "justify", "start", "end"]
      }],
      /**
       * Placeholder Color
       * @deprecated since Tailwind CSS v3.0.0
       * @see https://v3.tailwindcss.com/docs/placeholder-color
       */
      "placeholder-color": [{
        placeholder: scaleColor()
      }],
      /**
       * Text Color
       * @see https://tailwindcss.com/docs/text-color
       */
      "text-color": [{
        text: scaleColor()
      }],
      /**
       * Text Decoration
       * @see https://tailwindcss.com/docs/text-decoration
       */
      "text-decoration": ["underline", "overline", "line-through", "no-underline"],
      /**
       * Text Decoration Style
       * @see https://tailwindcss.com/docs/text-decoration-style
       */
      "text-decoration-style": [{
        decoration: [...scaleLineStyle(), "wavy"]
      }],
      /**
       * Text Decoration Thickness
       * @see https://tailwindcss.com/docs/text-decoration-thickness
       */
      "text-decoration-thickness": [{
        decoration: [isNumber, "from-font", "auto", isArbitraryVariable, isArbitraryLength]
      }],
      /**
       * Text Decoration Color
       * @see https://tailwindcss.com/docs/text-decoration-color
       */
      "text-decoration-color": [{
        decoration: scaleColor()
      }],
      /**
       * Text Underline Offset
       * @see https://tailwindcss.com/docs/text-underline-offset
       */
      "underline-offset": [{
        "underline-offset": [isNumber, "auto", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Text Transform
       * @see https://tailwindcss.com/docs/text-transform
       */
      "text-transform": ["uppercase", "lowercase", "capitalize", "normal-case"],
      /**
       * Text Overflow
       * @see https://tailwindcss.com/docs/text-overflow
       */
      "text-overflow": ["truncate", "text-ellipsis", "text-clip"],
      /**
       * Text Wrap
       * @see https://tailwindcss.com/docs/text-wrap
       */
      "text-wrap": [{
        text: ["wrap", "nowrap", "balance", "pretty"]
      }],
      /**
       * Text Indent
       * @see https://tailwindcss.com/docs/text-indent
       */
      indent: [{
        indent: scaleUnambiguousSpacing()
      }],
      /**
       * Vertical Alignment
       * @see https://tailwindcss.com/docs/vertical-align
       */
      "vertical-align": [{
        align: ["baseline", "top", "middle", "bottom", "text-top", "text-bottom", "sub", "super", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Whitespace
       * @see https://tailwindcss.com/docs/whitespace
       */
      whitespace: [{
        whitespace: ["normal", "nowrap", "pre", "pre-line", "pre-wrap", "break-spaces"]
      }],
      /**
       * Word Break
       * @see https://tailwindcss.com/docs/word-break
       */
      break: [{
        break: ["normal", "words", "all", "keep"]
      }],
      /**
       * Overflow Wrap
       * @see https://tailwindcss.com/docs/overflow-wrap
       */
      wrap: [{
        wrap: ["break-word", "anywhere", "normal"]
      }],
      /**
       * Hyphens
       * @see https://tailwindcss.com/docs/hyphens
       */
      hyphens: [{
        hyphens: ["none", "manual", "auto"]
      }],
      /**
       * Content
       * @see https://tailwindcss.com/docs/content
       */
      content: [{
        content: ["none", isArbitraryVariable, isArbitraryValue]
      }],
      // -------------------
      // --- Backgrounds ---
      // -------------------
      /**
       * Background Attachment
       * @see https://tailwindcss.com/docs/background-attachment
       */
      "bg-attachment": [{
        bg: ["fixed", "local", "scroll"]
      }],
      /**
       * Background Clip
       * @see https://tailwindcss.com/docs/background-clip
       */
      "bg-clip": [{
        "bg-clip": ["border", "padding", "content", "text"]
      }],
      /**
       * Background Origin
       * @see https://tailwindcss.com/docs/background-origin
       */
      "bg-origin": [{
        "bg-origin": ["border", "padding", "content"]
      }],
      /**
       * Background Position
       * @see https://tailwindcss.com/docs/background-position
       */
      "bg-position": [{
        bg: scaleBgPosition()
      }],
      /**
       * Background Repeat
       * @see https://tailwindcss.com/docs/background-repeat
       */
      "bg-repeat": [{
        bg: scaleBgRepeat()
      }],
      /**
       * Background Size
       * @see https://tailwindcss.com/docs/background-size
       */
      "bg-size": [{
        bg: scaleBgSize()
      }],
      /**
       * Background Image
       * @see https://tailwindcss.com/docs/background-image
       */
      "bg-image": [{
        bg: ["none", {
          linear: [{
            to: ["t", "tr", "r", "br", "b", "bl", "l", "tl"]
          }, isInteger, isArbitraryVariable, isArbitraryValue],
          radial: ["", isArbitraryVariable, isArbitraryValue],
          conic: [isInteger, isArbitraryVariable, isArbitraryValue]
        }, isArbitraryVariableImage, isArbitraryImage]
      }],
      /**
       * Background Color
       * @see https://tailwindcss.com/docs/background-color
       */
      "bg-color": [{
        bg: scaleColor()
      }],
      /**
       * Gradient Color Stops From Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-from-pos": [{
        from: scaleGradientStopPosition()
      }],
      /**
       * Gradient Color Stops Via Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-via-pos": [{
        via: scaleGradientStopPosition()
      }],
      /**
       * Gradient Color Stops To Position
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-to-pos": [{
        to: scaleGradientStopPosition()
      }],
      /**
       * Gradient Color Stops From
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-from": [{
        from: scaleColor()
      }],
      /**
       * Gradient Color Stops Via
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-via": [{
        via: scaleColor()
      }],
      /**
       * Gradient Color Stops To
       * @see https://tailwindcss.com/docs/gradient-color-stops
       */
      "gradient-to": [{
        to: scaleColor()
      }],
      // ---------------
      // --- Borders ---
      // ---------------
      /**
       * Border Radius
       * @see https://tailwindcss.com/docs/border-radius
       */
      rounded: [{
        rounded: scaleRadius()
      }],
      /**
       * Border Radius Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-s": [{
        "rounded-s": scaleRadius()
      }],
      /**
       * Border Radius End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-e": [{
        "rounded-e": scaleRadius()
      }],
      /**
       * Border Radius Top
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-t": [{
        "rounded-t": scaleRadius()
      }],
      /**
       * Border Radius Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-r": [{
        "rounded-r": scaleRadius()
      }],
      /**
       * Border Radius Bottom
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-b": [{
        "rounded-b": scaleRadius()
      }],
      /**
       * Border Radius Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-l": [{
        "rounded-l": scaleRadius()
      }],
      /**
       * Border Radius Start Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-ss": [{
        "rounded-ss": scaleRadius()
      }],
      /**
       * Border Radius Start End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-se": [{
        "rounded-se": scaleRadius()
      }],
      /**
       * Border Radius End End
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-ee": [{
        "rounded-ee": scaleRadius()
      }],
      /**
       * Border Radius End Start
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-es": [{
        "rounded-es": scaleRadius()
      }],
      /**
       * Border Radius Top Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-tl": [{
        "rounded-tl": scaleRadius()
      }],
      /**
       * Border Radius Top Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-tr": [{
        "rounded-tr": scaleRadius()
      }],
      /**
       * Border Radius Bottom Right
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-br": [{
        "rounded-br": scaleRadius()
      }],
      /**
       * Border Radius Bottom Left
       * @see https://tailwindcss.com/docs/border-radius
       */
      "rounded-bl": [{
        "rounded-bl": scaleRadius()
      }],
      /**
       * Border Width
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w": [{
        border: scaleBorderWidth()
      }],
      /**
       * Border Width X
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-x": [{
        "border-x": scaleBorderWidth()
      }],
      /**
       * Border Width Y
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-y": [{
        "border-y": scaleBorderWidth()
      }],
      /**
       * Border Width Start
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-s": [{
        "border-s": scaleBorderWidth()
      }],
      /**
       * Border Width End
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-e": [{
        "border-e": scaleBorderWidth()
      }],
      /**
       * Border Width Top
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-t": [{
        "border-t": scaleBorderWidth()
      }],
      /**
       * Border Width Right
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-r": [{
        "border-r": scaleBorderWidth()
      }],
      /**
       * Border Width Bottom
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-b": [{
        "border-b": scaleBorderWidth()
      }],
      /**
       * Border Width Left
       * @see https://tailwindcss.com/docs/border-width
       */
      "border-w-l": [{
        "border-l": scaleBorderWidth()
      }],
      /**
       * Divide Width X
       * @see https://tailwindcss.com/docs/border-width#between-children
       */
      "divide-x": [{
        "divide-x": scaleBorderWidth()
      }],
      /**
       * Divide Width X Reverse
       * @see https://tailwindcss.com/docs/border-width#between-children
       */
      "divide-x-reverse": ["divide-x-reverse"],
      /**
       * Divide Width Y
       * @see https://tailwindcss.com/docs/border-width#between-children
       */
      "divide-y": [{
        "divide-y": scaleBorderWidth()
      }],
      /**
       * Divide Width Y Reverse
       * @see https://tailwindcss.com/docs/border-width#between-children
       */
      "divide-y-reverse": ["divide-y-reverse"],
      /**
       * Border Style
       * @see https://tailwindcss.com/docs/border-style
       */
      "border-style": [{
        border: [...scaleLineStyle(), "hidden", "none"]
      }],
      /**
       * Divide Style
       * @see https://tailwindcss.com/docs/border-style#setting-the-divider-style
       */
      "divide-style": [{
        divide: [...scaleLineStyle(), "hidden", "none"]
      }],
      /**
       * Border Color
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color": [{
        border: scaleColor()
      }],
      /**
       * Border Color X
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-x": [{
        "border-x": scaleColor()
      }],
      /**
       * Border Color Y
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-y": [{
        "border-y": scaleColor()
      }],
      /**
       * Border Color S
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-s": [{
        "border-s": scaleColor()
      }],
      /**
       * Border Color E
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-e": [{
        "border-e": scaleColor()
      }],
      /**
       * Border Color Top
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-t": [{
        "border-t": scaleColor()
      }],
      /**
       * Border Color Right
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-r": [{
        "border-r": scaleColor()
      }],
      /**
       * Border Color Bottom
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-b": [{
        "border-b": scaleColor()
      }],
      /**
       * Border Color Left
       * @see https://tailwindcss.com/docs/border-color
       */
      "border-color-l": [{
        "border-l": scaleColor()
      }],
      /**
       * Divide Color
       * @see https://tailwindcss.com/docs/divide-color
       */
      "divide-color": [{
        divide: scaleColor()
      }],
      /**
       * Outline Style
       * @see https://tailwindcss.com/docs/outline-style
       */
      "outline-style": [{
        outline: [...scaleLineStyle(), "none", "hidden"]
      }],
      /**
       * Outline Offset
       * @see https://tailwindcss.com/docs/outline-offset
       */
      "outline-offset": [{
        "outline-offset": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Outline Width
       * @see https://tailwindcss.com/docs/outline-width
       */
      "outline-w": [{
        outline: ["", isNumber, isArbitraryVariableLength, isArbitraryLength]
      }],
      /**
       * Outline Color
       * @see https://tailwindcss.com/docs/outline-color
       */
      "outline-color": [{
        outline: scaleColor()
      }],
      // ---------------
      // --- Effects ---
      // ---------------
      /**
       * Box Shadow
       * @see https://tailwindcss.com/docs/box-shadow
       */
      shadow: [{
        shadow: [
          // Deprecated since Tailwind CSS v4.0.0
          "",
          "none",
          themeShadow,
          isArbitraryVariableShadow,
          isArbitraryShadow
        ]
      }],
      /**
       * Box Shadow Color
       * @see https://tailwindcss.com/docs/box-shadow#setting-the-shadow-color
       */
      "shadow-color": [{
        shadow: scaleColor()
      }],
      /**
       * Inset Box Shadow
       * @see https://tailwindcss.com/docs/box-shadow#adding-an-inset-shadow
       */
      "inset-shadow": [{
        "inset-shadow": ["none", themeInsetShadow, isArbitraryVariableShadow, isArbitraryShadow]
      }],
      /**
       * Inset Box Shadow Color
       * @see https://tailwindcss.com/docs/box-shadow#setting-the-inset-shadow-color
       */
      "inset-shadow-color": [{
        "inset-shadow": scaleColor()
      }],
      /**
       * Ring Width
       * @see https://tailwindcss.com/docs/box-shadow#adding-a-ring
       */
      "ring-w": [{
        ring: scaleBorderWidth()
      }],
      /**
       * Ring Width Inset
       * @see https://v3.tailwindcss.com/docs/ring-width#inset-rings
       * @deprecated since Tailwind CSS v4.0.0
       * @see https://github.com/tailwindlabs/tailwindcss/blob/v4.0.0/packages/tailwindcss/src/utilities.ts#L4158
       */
      "ring-w-inset": ["ring-inset"],
      /**
       * Ring Color
       * @see https://tailwindcss.com/docs/box-shadow#setting-the-ring-color
       */
      "ring-color": [{
        ring: scaleColor()
      }],
      /**
       * Ring Offset Width
       * @see https://v3.tailwindcss.com/docs/ring-offset-width
       * @deprecated since Tailwind CSS v4.0.0
       * @see https://github.com/tailwindlabs/tailwindcss/blob/v4.0.0/packages/tailwindcss/src/utilities.ts#L4158
       */
      "ring-offset-w": [{
        "ring-offset": [isNumber, isArbitraryLength]
      }],
      /**
       * Ring Offset Color
       * @see https://v3.tailwindcss.com/docs/ring-offset-color
       * @deprecated since Tailwind CSS v4.0.0
       * @see https://github.com/tailwindlabs/tailwindcss/blob/v4.0.0/packages/tailwindcss/src/utilities.ts#L4158
       */
      "ring-offset-color": [{
        "ring-offset": scaleColor()
      }],
      /**
       * Inset Ring Width
       * @see https://tailwindcss.com/docs/box-shadow#adding-an-inset-ring
       */
      "inset-ring-w": [{
        "inset-ring": scaleBorderWidth()
      }],
      /**
       * Inset Ring Color
       * @see https://tailwindcss.com/docs/box-shadow#setting-the-inset-ring-color
       */
      "inset-ring-color": [{
        "inset-ring": scaleColor()
      }],
      /**
       * Text Shadow
       * @see https://tailwindcss.com/docs/text-shadow
       */
      "text-shadow": [{
        "text-shadow": ["none", themeTextShadow, isArbitraryVariableShadow, isArbitraryShadow]
      }],
      /**
       * Text Shadow Color
       * @see https://tailwindcss.com/docs/text-shadow#setting-the-shadow-color
       */
      "text-shadow-color": [{
        "text-shadow": scaleColor()
      }],
      /**
       * Opacity
       * @see https://tailwindcss.com/docs/opacity
       */
      opacity: [{
        opacity: [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Mix Blend Mode
       * @see https://tailwindcss.com/docs/mix-blend-mode
       */
      "mix-blend": [{
        "mix-blend": [...scaleBlendMode(), "plus-darker", "plus-lighter"]
      }],
      /**
       * Background Blend Mode
       * @see https://tailwindcss.com/docs/background-blend-mode
       */
      "bg-blend": [{
        "bg-blend": scaleBlendMode()
      }],
      /**
       * Mask Clip
       * @see https://tailwindcss.com/docs/mask-clip
       */
      "mask-clip": [{
        "mask-clip": ["border", "padding", "content", "fill", "stroke", "view"]
      }, "mask-no-clip"],
      /**
       * Mask Composite
       * @see https://tailwindcss.com/docs/mask-composite
       */
      "mask-composite": [{
        mask: ["add", "subtract", "intersect", "exclude"]
      }],
      /**
       * Mask Image
       * @see https://tailwindcss.com/docs/mask-image
       */
      "mask-image-linear-pos": [{
        "mask-linear": [isNumber]
      }],
      "mask-image-linear-from-pos": [{
        "mask-linear-from": scaleMaskImagePosition()
      }],
      "mask-image-linear-to-pos": [{
        "mask-linear-to": scaleMaskImagePosition()
      }],
      "mask-image-linear-from-color": [{
        "mask-linear-from": scaleColor()
      }],
      "mask-image-linear-to-color": [{
        "mask-linear-to": scaleColor()
      }],
      "mask-image-t-from-pos": [{
        "mask-t-from": scaleMaskImagePosition()
      }],
      "mask-image-t-to-pos": [{
        "mask-t-to": scaleMaskImagePosition()
      }],
      "mask-image-t-from-color": [{
        "mask-t-from": scaleColor()
      }],
      "mask-image-t-to-color": [{
        "mask-t-to": scaleColor()
      }],
      "mask-image-r-from-pos": [{
        "mask-r-from": scaleMaskImagePosition()
      }],
      "mask-image-r-to-pos": [{
        "mask-r-to": scaleMaskImagePosition()
      }],
      "mask-image-r-from-color": [{
        "mask-r-from": scaleColor()
      }],
      "mask-image-r-to-color": [{
        "mask-r-to": scaleColor()
      }],
      "mask-image-b-from-pos": [{
        "mask-b-from": scaleMaskImagePosition()
      }],
      "mask-image-b-to-pos": [{
        "mask-b-to": scaleMaskImagePosition()
      }],
      "mask-image-b-from-color": [{
        "mask-b-from": scaleColor()
      }],
      "mask-image-b-to-color": [{
        "mask-b-to": scaleColor()
      }],
      "mask-image-l-from-pos": [{
        "mask-l-from": scaleMaskImagePosition()
      }],
      "mask-image-l-to-pos": [{
        "mask-l-to": scaleMaskImagePosition()
      }],
      "mask-image-l-from-color": [{
        "mask-l-from": scaleColor()
      }],
      "mask-image-l-to-color": [{
        "mask-l-to": scaleColor()
      }],
      "mask-image-x-from-pos": [{
        "mask-x-from": scaleMaskImagePosition()
      }],
      "mask-image-x-to-pos": [{
        "mask-x-to": scaleMaskImagePosition()
      }],
      "mask-image-x-from-color": [{
        "mask-x-from": scaleColor()
      }],
      "mask-image-x-to-color": [{
        "mask-x-to": scaleColor()
      }],
      "mask-image-y-from-pos": [{
        "mask-y-from": scaleMaskImagePosition()
      }],
      "mask-image-y-to-pos": [{
        "mask-y-to": scaleMaskImagePosition()
      }],
      "mask-image-y-from-color": [{
        "mask-y-from": scaleColor()
      }],
      "mask-image-y-to-color": [{
        "mask-y-to": scaleColor()
      }],
      "mask-image-radial": [{
        "mask-radial": [isArbitraryVariable, isArbitraryValue]
      }],
      "mask-image-radial-from-pos": [{
        "mask-radial-from": scaleMaskImagePosition()
      }],
      "mask-image-radial-to-pos": [{
        "mask-radial-to": scaleMaskImagePosition()
      }],
      "mask-image-radial-from-color": [{
        "mask-radial-from": scaleColor()
      }],
      "mask-image-radial-to-color": [{
        "mask-radial-to": scaleColor()
      }],
      "mask-image-radial-shape": [{
        "mask-radial": ["circle", "ellipse"]
      }],
      "mask-image-radial-size": [{
        "mask-radial": [{
          closest: ["side", "corner"],
          farthest: ["side", "corner"]
        }]
      }],
      "mask-image-radial-pos": [{
        "mask-radial-at": scalePosition()
      }],
      "mask-image-conic-pos": [{
        "mask-conic": [isNumber]
      }],
      "mask-image-conic-from-pos": [{
        "mask-conic-from": scaleMaskImagePosition()
      }],
      "mask-image-conic-to-pos": [{
        "mask-conic-to": scaleMaskImagePosition()
      }],
      "mask-image-conic-from-color": [{
        "mask-conic-from": scaleColor()
      }],
      "mask-image-conic-to-color": [{
        "mask-conic-to": scaleColor()
      }],
      /**
       * Mask Mode
       * @see https://tailwindcss.com/docs/mask-mode
       */
      "mask-mode": [{
        mask: ["alpha", "luminance", "match"]
      }],
      /**
       * Mask Origin
       * @see https://tailwindcss.com/docs/mask-origin
       */
      "mask-origin": [{
        "mask-origin": ["border", "padding", "content", "fill", "stroke", "view"]
      }],
      /**
       * Mask Position
       * @see https://tailwindcss.com/docs/mask-position
       */
      "mask-position": [{
        mask: scaleBgPosition()
      }],
      /**
       * Mask Repeat
       * @see https://tailwindcss.com/docs/mask-repeat
       */
      "mask-repeat": [{
        mask: scaleBgRepeat()
      }],
      /**
       * Mask Size
       * @see https://tailwindcss.com/docs/mask-size
       */
      "mask-size": [{
        mask: scaleBgSize()
      }],
      /**
       * Mask Type
       * @see https://tailwindcss.com/docs/mask-type
       */
      "mask-type": [{
        "mask-type": ["alpha", "luminance"]
      }],
      /**
       * Mask Image
       * @see https://tailwindcss.com/docs/mask-image
       */
      "mask-image": [{
        mask: ["none", isArbitraryVariable, isArbitraryValue]
      }],
      // ---------------
      // --- Filters ---
      // ---------------
      /**
       * Filter
       * @see https://tailwindcss.com/docs/filter
       */
      filter: [{
        filter: [
          // Deprecated since Tailwind CSS v3.0.0
          "",
          "none",
          isArbitraryVariable,
          isArbitraryValue
        ]
      }],
      /**
       * Blur
       * @see https://tailwindcss.com/docs/blur
       */
      blur: [{
        blur: scaleBlur()
      }],
      /**
       * Brightness
       * @see https://tailwindcss.com/docs/brightness
       */
      brightness: [{
        brightness: [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Contrast
       * @see https://tailwindcss.com/docs/contrast
       */
      contrast: [{
        contrast: [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Drop Shadow
       * @see https://tailwindcss.com/docs/drop-shadow
       */
      "drop-shadow": [{
        "drop-shadow": [
          // Deprecated since Tailwind CSS v4.0.0
          "",
          "none",
          themeDropShadow,
          isArbitraryVariableShadow,
          isArbitraryShadow
        ]
      }],
      /**
       * Drop Shadow Color
       * @see https://tailwindcss.com/docs/filter-drop-shadow#setting-the-shadow-color
       */
      "drop-shadow-color": [{
        "drop-shadow": scaleColor()
      }],
      /**
       * Grayscale
       * @see https://tailwindcss.com/docs/grayscale
       */
      grayscale: [{
        grayscale: ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Hue Rotate
       * @see https://tailwindcss.com/docs/hue-rotate
       */
      "hue-rotate": [{
        "hue-rotate": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Invert
       * @see https://tailwindcss.com/docs/invert
       */
      invert: [{
        invert: ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Saturate
       * @see https://tailwindcss.com/docs/saturate
       */
      saturate: [{
        saturate: [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Sepia
       * @see https://tailwindcss.com/docs/sepia
       */
      sepia: [{
        sepia: ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Filter
       * @see https://tailwindcss.com/docs/backdrop-filter
       */
      "backdrop-filter": [{
        "backdrop-filter": [
          // Deprecated since Tailwind CSS v3.0.0
          "",
          "none",
          isArbitraryVariable,
          isArbitraryValue
        ]
      }],
      /**
       * Backdrop Blur
       * @see https://tailwindcss.com/docs/backdrop-blur
       */
      "backdrop-blur": [{
        "backdrop-blur": scaleBlur()
      }],
      /**
       * Backdrop Brightness
       * @see https://tailwindcss.com/docs/backdrop-brightness
       */
      "backdrop-brightness": [{
        "backdrop-brightness": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Contrast
       * @see https://tailwindcss.com/docs/backdrop-contrast
       */
      "backdrop-contrast": [{
        "backdrop-contrast": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Grayscale
       * @see https://tailwindcss.com/docs/backdrop-grayscale
       */
      "backdrop-grayscale": [{
        "backdrop-grayscale": ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Hue Rotate
       * @see https://tailwindcss.com/docs/backdrop-hue-rotate
       */
      "backdrop-hue-rotate": [{
        "backdrop-hue-rotate": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Invert
       * @see https://tailwindcss.com/docs/backdrop-invert
       */
      "backdrop-invert": [{
        "backdrop-invert": ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Opacity
       * @see https://tailwindcss.com/docs/backdrop-opacity
       */
      "backdrop-opacity": [{
        "backdrop-opacity": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Saturate
       * @see https://tailwindcss.com/docs/backdrop-saturate
       */
      "backdrop-saturate": [{
        "backdrop-saturate": [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Backdrop Sepia
       * @see https://tailwindcss.com/docs/backdrop-sepia
       */
      "backdrop-sepia": [{
        "backdrop-sepia": ["", isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      // --------------
      // --- Tables ---
      // --------------
      /**
       * Border Collapse
       * @see https://tailwindcss.com/docs/border-collapse
       */
      "border-collapse": [{
        border: ["collapse", "separate"]
      }],
      /**
       * Border Spacing
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing": [{
        "border-spacing": scaleUnambiguousSpacing()
      }],
      /**
       * Border Spacing X
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing-x": [{
        "border-spacing-x": scaleUnambiguousSpacing()
      }],
      /**
       * Border Spacing Y
       * @see https://tailwindcss.com/docs/border-spacing
       */
      "border-spacing-y": [{
        "border-spacing-y": scaleUnambiguousSpacing()
      }],
      /**
       * Table Layout
       * @see https://tailwindcss.com/docs/table-layout
       */
      "table-layout": [{
        table: ["auto", "fixed"]
      }],
      /**
       * Caption Side
       * @see https://tailwindcss.com/docs/caption-side
       */
      caption: [{
        caption: ["top", "bottom"]
      }],
      // ---------------------------------
      // --- Transitions and Animation ---
      // ---------------------------------
      /**
       * Transition Property
       * @see https://tailwindcss.com/docs/transition-property
       */
      transition: [{
        transition: ["", "all", "colors", "opacity", "shadow", "transform", "none", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Transition Behavior
       * @see https://tailwindcss.com/docs/transition-behavior
       */
      "transition-behavior": [{
        transition: ["normal", "discrete"]
      }],
      /**
       * Transition Duration
       * @see https://tailwindcss.com/docs/transition-duration
       */
      duration: [{
        duration: [isNumber, "initial", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Transition Timing Function
       * @see https://tailwindcss.com/docs/transition-timing-function
       */
      ease: [{
        ease: ["linear", "initial", themeEase, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Transition Delay
       * @see https://tailwindcss.com/docs/transition-delay
       */
      delay: [{
        delay: [isNumber, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Animation
       * @see https://tailwindcss.com/docs/animation
       */
      animate: [{
        animate: ["none", themeAnimate, isArbitraryVariable, isArbitraryValue]
      }],
      // ------------------
      // --- Transforms ---
      // ------------------
      /**
       * Backface Visibility
       * @see https://tailwindcss.com/docs/backface-visibility
       */
      backface: [{
        backface: ["hidden", "visible"]
      }],
      /**
       * Perspective
       * @see https://tailwindcss.com/docs/perspective
       */
      perspective: [{
        perspective: [themePerspective, isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Perspective Origin
       * @see https://tailwindcss.com/docs/perspective-origin
       */
      "perspective-origin": [{
        "perspective-origin": scalePositionWithArbitrary()
      }],
      /**
       * Rotate
       * @see https://tailwindcss.com/docs/rotate
       */
      rotate: [{
        rotate: scaleRotate()
      }],
      /**
       * Rotate X
       * @see https://tailwindcss.com/docs/rotate
       */
      "rotate-x": [{
        "rotate-x": scaleRotate()
      }],
      /**
       * Rotate Y
       * @see https://tailwindcss.com/docs/rotate
       */
      "rotate-y": [{
        "rotate-y": scaleRotate()
      }],
      /**
       * Rotate Z
       * @see https://tailwindcss.com/docs/rotate
       */
      "rotate-z": [{
        "rotate-z": scaleRotate()
      }],
      /**
       * Scale
       * @see https://tailwindcss.com/docs/scale
       */
      scale: [{
        scale: scaleScale()
      }],
      /**
       * Scale X
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-x": [{
        "scale-x": scaleScale()
      }],
      /**
       * Scale Y
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-y": [{
        "scale-y": scaleScale()
      }],
      /**
       * Scale Z
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-z": [{
        "scale-z": scaleScale()
      }],
      /**
       * Scale 3D
       * @see https://tailwindcss.com/docs/scale
       */
      "scale-3d": ["scale-3d"],
      /**
       * Skew
       * @see https://tailwindcss.com/docs/skew
       */
      skew: [{
        skew: scaleSkew()
      }],
      /**
       * Skew X
       * @see https://tailwindcss.com/docs/skew
       */
      "skew-x": [{
        "skew-x": scaleSkew()
      }],
      /**
       * Skew Y
       * @see https://tailwindcss.com/docs/skew
       */
      "skew-y": [{
        "skew-y": scaleSkew()
      }],
      /**
       * Transform
       * @see https://tailwindcss.com/docs/transform
       */
      transform: [{
        transform: [isArbitraryVariable, isArbitraryValue, "", "none", "gpu", "cpu"]
      }],
      /**
       * Transform Origin
       * @see https://tailwindcss.com/docs/transform-origin
       */
      "transform-origin": [{
        origin: scalePositionWithArbitrary()
      }],
      /**
       * Transform Style
       * @see https://tailwindcss.com/docs/transform-style
       */
      "transform-style": [{
        transform: ["3d", "flat"]
      }],
      /**
       * Translate
       * @see https://tailwindcss.com/docs/translate
       */
      translate: [{
        translate: scaleTranslate()
      }],
      /**
       * Translate X
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-x": [{
        "translate-x": scaleTranslate()
      }],
      /**
       * Translate Y
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-y": [{
        "translate-y": scaleTranslate()
      }],
      /**
       * Translate Z
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-z": [{
        "translate-z": scaleTranslate()
      }],
      /**
       * Translate None
       * @see https://tailwindcss.com/docs/translate
       */
      "translate-none": ["translate-none"],
      // ---------------------
      // --- Interactivity ---
      // ---------------------
      /**
       * Accent Color
       * @see https://tailwindcss.com/docs/accent-color
       */
      accent: [{
        accent: scaleColor()
      }],
      /**
       * Appearance
       * @see https://tailwindcss.com/docs/appearance
       */
      appearance: [{
        appearance: ["none", "auto"]
      }],
      /**
       * Caret Color
       * @see https://tailwindcss.com/docs/just-in-time-mode#caret-color-utilities
       */
      "caret-color": [{
        caret: scaleColor()
      }],
      /**
       * Color Scheme
       * @see https://tailwindcss.com/docs/color-scheme
       */
      "color-scheme": [{
        scheme: ["normal", "dark", "light", "light-dark", "only-dark", "only-light"]
      }],
      /**
       * Cursor
       * @see https://tailwindcss.com/docs/cursor
       */
      cursor: [{
        cursor: ["auto", "default", "pointer", "wait", "text", "move", "help", "not-allowed", "none", "context-menu", "progress", "cell", "crosshair", "vertical-text", "alias", "copy", "no-drop", "grab", "grabbing", "all-scroll", "col-resize", "row-resize", "n-resize", "e-resize", "s-resize", "w-resize", "ne-resize", "nw-resize", "se-resize", "sw-resize", "ew-resize", "ns-resize", "nesw-resize", "nwse-resize", "zoom-in", "zoom-out", isArbitraryVariable, isArbitraryValue]
      }],
      /**
       * Field Sizing
       * @see https://tailwindcss.com/docs/field-sizing
       */
      "field-sizing": [{
        "field-sizing": ["fixed", "content"]
      }],
      /**
       * Pointer Events
       * @see https://tailwindcss.com/docs/pointer-events
       */
      "pointer-events": [{
        "pointer-events": ["auto", "none"]
      }],
      /**
       * Resize
       * @see https://tailwindcss.com/docs/resize
       */
      resize: [{
        resize: ["none", "", "y", "x"]
      }],
      /**
       * Scroll Behavior
       * @see https://tailwindcss.com/docs/scroll-behavior
       */
      "scroll-behavior": [{
        scroll: ["auto", "smooth"]
      }],
      /**
       * Scroll Margin
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-m": [{
        "scroll-m": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin X
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mx": [{
        "scroll-mx": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Y
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-my": [{
        "scroll-my": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Start
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-ms": [{
        "scroll-ms": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin End
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-me": [{
        "scroll-me": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Top
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mt": [{
        "scroll-mt": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Right
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mr": [{
        "scroll-mr": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Bottom
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-mb": [{
        "scroll-mb": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Margin Left
       * @see https://tailwindcss.com/docs/scroll-margin
       */
      "scroll-ml": [{
        "scroll-ml": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-p": [{
        "scroll-p": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding X
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-px": [{
        "scroll-px": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Y
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-py": [{
        "scroll-py": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Start
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-ps": [{
        "scroll-ps": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding End
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pe": [{
        "scroll-pe": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Top
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pt": [{
        "scroll-pt": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Right
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pr": [{
        "scroll-pr": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Bottom
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pb": [{
        "scroll-pb": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Padding Left
       * @see https://tailwindcss.com/docs/scroll-padding
       */
      "scroll-pl": [{
        "scroll-pl": scaleUnambiguousSpacing()
      }],
      /**
       * Scroll Snap Align
       * @see https://tailwindcss.com/docs/scroll-snap-align
       */
      "snap-align": [{
        snap: ["start", "end", "center", "align-none"]
      }],
      /**
       * Scroll Snap Stop
       * @see https://tailwindcss.com/docs/scroll-snap-stop
       */
      "snap-stop": [{
        snap: ["normal", "always"]
      }],
      /**
       * Scroll Snap Type
       * @see https://tailwindcss.com/docs/scroll-snap-type
       */
      "snap-type": [{
        snap: ["none", "x", "y", "both"]
      }],
      /**
       * Scroll Snap Type Strictness
       * @see https://tailwindcss.com/docs/scroll-snap-type
       */
      "snap-strictness": [{
        snap: ["mandatory", "proximity"]
      }],
      /**
       * Touch Action
       * @see https://tailwindcss.com/docs/touch-action
       */
      touch: [{
        touch: ["auto", "none", "manipulation"]
      }],
      /**
       * Touch Action X
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-x": [{
        "touch-pan": ["x", "left", "right"]
      }],
      /**
       * Touch Action Y
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-y": [{
        "touch-pan": ["y", "up", "down"]
      }],
      /**
       * Touch Action Pinch Zoom
       * @see https://tailwindcss.com/docs/touch-action
       */
      "touch-pz": ["touch-pinch-zoom"],
      /**
       * User Select
       * @see https://tailwindcss.com/docs/user-select
       */
      select: [{
        select: ["none", "text", "all", "auto"]
      }],
      /**
       * Will Change
       * @see https://tailwindcss.com/docs/will-change
       */
      "will-change": [{
        "will-change": ["auto", "scroll", "contents", "transform", isArbitraryVariable, isArbitraryValue]
      }],
      // -----------
      // --- SVG ---
      // -----------
      /**
       * Fill
       * @see https://tailwindcss.com/docs/fill
       */
      fill: [{
        fill: ["none", ...scaleColor()]
      }],
      /**
       * Stroke Width
       * @see https://tailwindcss.com/docs/stroke-width
       */
      "stroke-w": [{
        stroke: [isNumber, isArbitraryVariableLength, isArbitraryLength, isArbitraryNumber]
      }],
      /**
       * Stroke
       * @see https://tailwindcss.com/docs/stroke
       */
      stroke: [{
        stroke: ["none", ...scaleColor()]
      }],
      // ---------------------
      // --- Accessibility ---
      // ---------------------
      /**
       * Forced Color Adjust
       * @see https://tailwindcss.com/docs/forced-color-adjust
       */
      "forced-color-adjust": [{
        "forced-color-adjust": ["auto", "none"]
      }]
    },
    conflictingClassGroups: {
      overflow: ["overflow-x", "overflow-y"],
      overscroll: ["overscroll-x", "overscroll-y"],
      inset: ["inset-x", "inset-y", "start", "end", "top", "right", "bottom", "left"],
      "inset-x": ["right", "left"],
      "inset-y": ["top", "bottom"],
      flex: ["basis", "grow", "shrink"],
      gap: ["gap-x", "gap-y"],
      p: ["px", "py", "ps", "pe", "pt", "pr", "pb", "pl"],
      px: ["pr", "pl"],
      py: ["pt", "pb"],
      m: ["mx", "my", "ms", "me", "mt", "mr", "mb", "ml"],
      mx: ["mr", "ml"],
      my: ["mt", "mb"],
      size: ["w", "h"],
      "font-size": ["leading"],
      "fvn-normal": ["fvn-ordinal", "fvn-slashed-zero", "fvn-figure", "fvn-spacing", "fvn-fraction"],
      "fvn-ordinal": ["fvn-normal"],
      "fvn-slashed-zero": ["fvn-normal"],
      "fvn-figure": ["fvn-normal"],
      "fvn-spacing": ["fvn-normal"],
      "fvn-fraction": ["fvn-normal"],
      "line-clamp": ["display", "overflow"],
      rounded: ["rounded-s", "rounded-e", "rounded-t", "rounded-r", "rounded-b", "rounded-l", "rounded-ss", "rounded-se", "rounded-ee", "rounded-es", "rounded-tl", "rounded-tr", "rounded-br", "rounded-bl"],
      "rounded-s": ["rounded-ss", "rounded-es"],
      "rounded-e": ["rounded-se", "rounded-ee"],
      "rounded-t": ["rounded-tl", "rounded-tr"],
      "rounded-r": ["rounded-tr", "rounded-br"],
      "rounded-b": ["rounded-br", "rounded-bl"],
      "rounded-l": ["rounded-tl", "rounded-bl"],
      "border-spacing": ["border-spacing-x", "border-spacing-y"],
      "border-w": ["border-w-x", "border-w-y", "border-w-s", "border-w-e", "border-w-t", "border-w-r", "border-w-b", "border-w-l"],
      "border-w-x": ["border-w-r", "border-w-l"],
      "border-w-y": ["border-w-t", "border-w-b"],
      "border-color": ["border-color-x", "border-color-y", "border-color-s", "border-color-e", "border-color-t", "border-color-r", "border-color-b", "border-color-l"],
      "border-color-x": ["border-color-r", "border-color-l"],
      "border-color-y": ["border-color-t", "border-color-b"],
      translate: ["translate-x", "translate-y", "translate-none"],
      "translate-none": ["translate", "translate-x", "translate-y", "translate-z"],
      "scroll-m": ["scroll-mx", "scroll-my", "scroll-ms", "scroll-me", "scroll-mt", "scroll-mr", "scroll-mb", "scroll-ml"],
      "scroll-mx": ["scroll-mr", "scroll-ml"],
      "scroll-my": ["scroll-mt", "scroll-mb"],
      "scroll-p": ["scroll-px", "scroll-py", "scroll-ps", "scroll-pe", "scroll-pt", "scroll-pr", "scroll-pb", "scroll-pl"],
      "scroll-px": ["scroll-pr", "scroll-pl"],
      "scroll-py": ["scroll-pt", "scroll-pb"],
      touch: ["touch-x", "touch-y", "touch-pz"],
      "touch-x": ["touch"],
      "touch-y": ["touch"],
      "touch-pz": ["touch"]
    },
    conflictingClassGroupModifiers: {
      "font-size": ["leading"]
    },
    orderSensitiveModifiers: ["*", "**", "after", "backdrop", "before", "details-content", "file", "first-letter", "first-line", "marker", "placeholder", "selection"]
  };
};
const twMerge = /* @__PURE__ */ createTailwindMerge(getDefaultConfig);
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
const SIGNUP_URL = "/register";
const LOGIN_URL = "/login";
const CONTACT_EMAIL = "mamadoufinances@gmail.com";
function LogoMark({ className }) {
  return /* @__PURE__ */ jsx("img", { src: "/assets/brand/Logo-nsara.jpg", alt: "", width: "1254", height: "1254", className: cn("object-contain", className), "aria-hidden": "true" });
}
function Logo({ className, href = "#top" }) {
  return /* @__PURE__ */ jsxs("a", { href, "aria-label": "NSARA — Retour en haut de page", className: cn("group inline-flex items-center gap-2.5", className), children: [
    /* @__PURE__ */ jsx(LogoMark, { className: "size-9 transition-transform duration-500 ease-out-expo group-hover:rotate-[-8deg] group-hover:scale-105 sm:size-10" }),
    /* @__PURE__ */ jsx("span", { className: "font-display text-[1.45rem] font-extrabold tracking-[-0.03em] text-ink sm:text-[1.6rem]", children: "NSARA" })
  ] });
}
const base = "group relative inline-flex select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-xl font-semibold tracking-tight transition-all duration-300 ease-out-expo active:scale-[0.97]";
const variants = {
  primary: "sheen bg-forest-700 text-white shadow-[0_10px_24px_-10px_rgba(47,84,67,0.7),inset_0_1px_0_rgba(255,255,255,0.18)] hover:-translate-y-0.5 hover:bg-forest-800 hover:shadow-[0_18px_34px_-12px_rgba(47,84,67,0.75),inset_0_1px_0_rgba(255,255,255,0.18)]",
  secondary: "border border-forest-900/10 bg-white text-ink shadow-[0_1px_2px_rgba(19,33,26,0.06)] hover:-translate-y-0.5 hover:border-forest-900/25 hover:shadow-[0_14px_28px_-14px_rgba(19,33,26,0.25)]",
  light: "sheen bg-white text-forest-900 shadow-[0_14px_30px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5 hover:bg-forest-50",
  ghost: "text-ink hover:bg-forest-900/5",
  outlineLight: "border border-white/20 bg-white/5 text-white hover:-translate-y-0.5 hover:bg-white/10"
};
const sizes = {
  md: "h-11 px-5 text-[15px]",
  lg: "h-14 px-7 text-base"
};
function Button(props) {
  const { variant = "primary", size = "md", arrow = false, className, children, ...rest } = props;
  const classes = cn(base, variants[variant], sizes[size], className);
  const content = /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs("span", { className: "relative z-10 inline-flex items-center gap-2", children: [
    children,
    arrow && /* @__PURE__ */ jsx(ArrowRight, { "aria-hidden": "true", className: "size-[1.1em] transition-transform duration-300 ease-out-expo group-hover:translate-x-1" })
  ] }) });
  if ("href" in rest && rest.href !== void 0) {
    const { href, ...anchorRest } = rest;
    return /* @__PURE__ */ jsx("a", { href, className: classes, ...anchorRest, children: content });
  }
  const { href: _unused, ...buttonRest } = rest;
  return /* @__PURE__ */ jsx("button", { type: "button", className: classes, ...buttonRest, children: content });
}
const links = [
  { href: "#fonctionnalites", label: "Fonctionnalités" },
  { href: "#solutions", label: "Solutions" },
  { href: "#accompagnement", label: "Accompagnement" },
  { href: "#faq", label: "FAQ" }
];
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const progressRef = useRef(null);
  const toggleRef = useRef(null);
  const navRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      setScrolled(y > 12);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      let current = "";
      for (const l of links) {
        const el = document.querySelector(l.href);
        if (!el) continue;
        const r2 = el.getBoundingClientRect();
        if (r2.top <= 160 && r2.bottom > 160) current = l.href;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);
  return /* @__PURE__ */ jsx("header", { className: "fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-4", children: /* @__PURE__ */ jsxs(
    "nav",
    {
      ref: navRef,
      "aria-label": "Navigation principale",
      className: cn(
        "relative mx-auto max-w-7xl rounded-2xl border transition-all duration-500 ease-out-expo",
        scrolled || open ? "glass border-white/80 shadow-[0_18px_50px_-22px_rgba(19,33,26,0.35)]" : "border-forest-900/[0.06] bg-white/70 shadow-[0_2px_10px_-4px_rgba(19,33,26,0.08)] backdrop-blur-md"
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex h-16 items-center justify-between gap-4 pl-4 pr-2.5 sm:h-[4.5rem] sm:pl-6 sm:pr-3", children: [
          /* @__PURE__ */ jsx(Logo, {}),
          /* @__PURE__ */ jsx("ul", { className: "hidden items-center gap-1 xl:flex", children: links.map((l) => {
            const isActive = active === l.href;
            return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
              "a",
              {
                href: l.href,
                "aria-current": isActive ? "location" : void 0,
                className: cn(
                  "relative rounded-lg px-3.5 py-2 text-[15px] font-semibold transition-colors duration-300 hover:bg-forest-900/[0.045]",
                  isActive ? "text-forest-700" : "text-ink/75 hover:text-ink"
                ),
                children: [
                  l.label,
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      "aria-hidden": "true",
                      className: cn(
                        "absolute inset-x-3.5 -bottom-0.5 h-0.5 origin-left rounded-full bg-forest-600 transition-transform duration-500 ease-out-expo",
                        isActive ? "scale-x-100" : "scale-x-0"
                      )
                    }
                  )
                ]
              }
            ) }, l.href);
          }) }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 sm:gap-2", children: [
            /* @__PURE__ */ jsx(
              "a",
              {
                href: LOGIN_URL,
                className: "hidden rounded-lg px-3 py-2 text-[15px] font-semibold text-ink/80 transition-colors hover:bg-forest-900/[0.045] hover:text-ink sm:inline-flex",
                children: "Connexion"
              }
            ),
            /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, arrow: true, className: "hidden sm:inline-flex xl:hidden", children: "Créer un compte" }),
            /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, arrow: true, className: "hidden xl:inline-flex", children: "Créer un compte gratuitement" }),
            /* @__PURE__ */ jsx(
              "button",
              {
                ref: toggleRef,
                type: "button",
                "aria-expanded": open,
                "aria-controls": "menu-mobile",
                "aria-label": open ? "Fermer le menu" : "Ouvrir le menu",
                onClick: () => setOpen((v) => !v),
                className: "grid size-11 place-items-center rounded-xl text-ink transition-colors hover:bg-forest-900/5 xl:hidden",
                children: /* @__PURE__ */ jsxs("span", { className: "relative size-5", children: [
                  /* @__PURE__ */ jsx(Menu, { className: cn("absolute inset-0 size-5 transition-all duration-300", open ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100") }),
                  /* @__PURE__ */ jsx(X, { className: cn("absolute inset-0 size-5 transition-all duration-300", open ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0") })
                ] })
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "pointer-events-none absolute inset-x-5 bottom-0 h-px overflow-hidden rounded-full", children: /* @__PURE__ */ jsx("span", { ref: progressRef, className: "block h-full origin-left scale-x-0 bg-linear-to-r from-forest-300 via-forest-600 to-forest-300" }) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            id: "menu-mobile",
            inert: !open ? "" : void 0,
            className: cn("grid transition-[grid-template-rows,opacity] duration-500 ease-out-expo xl:hidden", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"),
            children: /* @__PURE__ */ jsxs("div", { className: "overflow-hidden", children: [
              /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1 px-3 pb-2 pt-1", children: links.map((l, i) => /* @__PURE__ */ jsx("li", { style: { transitionDelay: open ? `${80 + i * 45}ms` : "0ms" }, className: cn("transition-all duration-500 ease-out-expo", open ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"), children: /* @__PURE__ */ jsxs(
                "a",
                {
                  href: l.href,
                  onClick: () => setOpen(false),
                  className: cn(
                    "flex items-center justify-between rounded-xl px-4 py-3.5 text-base font-semibold transition-colors hover:bg-forest-900/5",
                    active === l.href ? "bg-forest-50 text-forest-700" : "text-ink"
                  ),
                  children: [
                    l.label,
                    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "text-forest-400", children: "→" })
                  ]
                }
              ) }, l.href)) }),
              /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2.5 border-t border-forest-900/[0.06] p-4", children: [
                /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, size: "lg", arrow: true, className: "w-full", children: "Créer un compte gratuitement" }),
                /* @__PURE__ */ jsx(Button, { href: LOGIN_URL, variant: "secondary", size: "lg", className: "w-full", children: "Connexion" })
              ] })
            ] })
          }
        )
      ]
    }
  ) });
}
const width = 2408;
const height = 1366;
const meta = {
  width,
  height
};
const dashboardSrc = "/assets/screens/dashboard.webp";
const hasShot = dashboardSrc !== null;
let dims = { width: 2862, height: 1624 };
const listeners = /* @__PURE__ */ new Set();
const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const useShotDims = () => useSyncExternalStore(
  subscribe,
  () => dims,
  () => dims
);
const regions = {
  /** Carte « Dernières ventes » */
  sales: { x: 0.195, y: 0.586, w: 0.45, h: 0.414 },
  /** Carte « À garder à l'œil » (stock, encaissements, clients, marge, valeur du stock) */
  watch: { x: 0.66, y: 0.586, w: 0.34, h: 0.414 },
  /** Bouton « Nouvelle vente » + sélecteur « Toutes les boutiques » */
  actions: { x: 0.835, y: 0.143, w: 0.165, h: 0.187 },
  /** Indicateur « Créances clients » */
  debts: { x: 0.79, y: 0.379, w: 0.20, h: 0.18 },
  /** Menu latéral : rubrique « Vos finances » (Factures, Comptabilité, Créances) */
  finances: { x: 0, y: 0.52, w: 0.173, h: 0.20 },
  /** Menu latéral complet, du logo à « Bibliothèque » */
  sidebar: { x: 0, y: 0.02, w: 0.173, h: 0.805 }
};
const focuses = {
  overview: { z: 1, cx: 0.5, cy: 0.5 },
  kpis: { z: 2.5, cx: 0.39, cy: 0.468 },
  sales: { z: 2.1, cx: 0.418, cy: 0.762 },
  watch: { z: 2.85, cx: 0.8275, cy: 0.777 }
};
function focusTransform({ z, cx, cy }) {
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const tx = clamp(50 - cx * z * 100, 100 - z * 100, 0);
  const ty = clamp(50 - cy * z * 100, 100 - z * 100, 0);
  return `translate3d(${tx}%, ${ty}%, 0) scale(${z})`;
}
function Container({ children, className }) {
  return /* @__PURE__ */ jsx("div", { className: cn("mx-auto w-full max-w-7xl px-5 sm:px-8", className), children });
}
function prefersReducedMotion() {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}
function skipMotion() {
  if (typeof window === "undefined") return true;
  return prefersReducedMotion() || navigator.webdriver === true || !("IntersectionObserver" in window);
}
function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(() => skipMotion());
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, threshold, rootMargin]);
  return [ref, inView];
}
function Reveal({ as: Tag = "div", delay: delay2 = 0, variant = "up", className, style, children, ...rest }) {
  const [ref, visible] = useInView();
  return /* @__PURE__ */ jsx(
    Tag,
    {
      ref,
      "data-visible": visible,
      "data-variant": variant,
      className: cn("reveal", className),
      style: { "--reveal-delay": `${delay2}ms`, ...style },
      ...rest,
      children
    }
  );
}
function Eyebrow({ children, tone = "light", className }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-2.5 text-[0.72rem] font-bold uppercase tracking-[0.2em] sm:text-xs",
        tone === "light" ? "text-forest-600" : "text-forest-300",
        className
      ),
      children: [
        /* @__PURE__ */ jsx("span", { className: cn("grid size-5 place-items-center rounded-full", tone === "light" ? "bg-forest-100" : "bg-white/10"), "aria-hidden": "true", children: /* @__PURE__ */ jsx("span", { className: cn("size-2 rounded-full", tone === "light" ? "bg-forest-600" : "bg-forest-300") }) }),
        children
      ]
    }
  );
}
function SectionHeading({ eyebrow, title, description, align = "center", tone = "light", className, titleId }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-5", align === "center" ? "mx-auto max-w-3xl items-center text-center" : "items-start text-left", className), children: [
    /* @__PURE__ */ jsx(Reveal, { children: /* @__PURE__ */ jsx(Eyebrow, { tone, children: eyebrow }) }),
    /* @__PURE__ */ jsx(Reveal, { delay: 80, children: /* @__PURE__ */ jsx(
      "h2",
      {
        id: titleId,
        className: cn(
          "text-balance font-display text-[2.4rem] font-extrabold leading-[1.02] tracking-[-0.03em] sm:text-5xl lg:text-[3.6rem]",
          tone === "light" ? "text-ink" : "text-white"
        ),
        children: title
      }
    ) }),
    description && /* @__PURE__ */ jsx(Reveal, { delay: 160, children: /* @__PURE__ */ jsx("p", { className: cn("text-pretty text-base leading-relaxed sm:text-lg", tone === "light" ? "text-ink-soft" : "text-forest-200/80"), children: description }) })
  ] });
}
function ShotSkeleton({ className }) {
  return /* @__PURE__ */ jsxs("div", { "aria-hidden": "true", className: cn("absolute inset-0 flex overflow-hidden bg-[#f6f5f2] transition-opacity duration-700", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex w-[17.3%] flex-col gap-[3.5%] bg-forest-900 p-[1.8%]", children: [
      /* @__PURE__ */ jsx("div", { className: "h-[6.5%] w-[70%] rounded-md bg-white/15" }),
      /* @__PURE__ */ jsx("div", { className: "mt-[8%] h-[5.5%] rounded-lg bg-white/10" }),
      Array.from({ length: 7 }).map((_, i) => /* @__PURE__ */ jsx("div", { className: "h-[3.2%] w-[75%] rounded bg-white/[0.07]" }, i))
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col gap-[3%] p-[2.6%]", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between", children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-[1.5%]", children: [
          /* @__PURE__ */ jsx("div", { className: "h-3 w-24 rounded bg-forest-900/10 sm:h-4 sm:w-40" }),
          /* @__PURE__ */ jsx("div", { className: "h-2 w-32 rounded bg-forest-900/[0.07] sm:h-3 sm:w-56" })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "h-5 w-16 rounded-md bg-forest-700/25 sm:h-8 sm:w-28" })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-4 gap-[1.6%]", children: Array.from({ length: 4 }).map((_, i) => /* @__PURE__ */ jsx("div", { className: "h-10 rounded-lg border border-forest-900/[0.07] bg-white sm:h-20" }, i)) }),
      /* @__PURE__ */ jsxs("div", { className: "grid flex-1 grid-cols-[1.4fr_1fr] gap-[1.6%]", children: [
        /* @__PURE__ */ jsx("div", { className: "rounded-lg border border-forest-900/[0.07] bg-white" }),
        /* @__PURE__ */ jsx("div", { className: "rounded-lg border border-forest-900/[0.07] bg-white" })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "absolute inset-0 animate-shimmer bg-linear-to-r from-transparent via-white/60 to-transparent" })
  ] });
}
function useImageLoaded() {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  }, []);
  return { ref, loaded, onLoad: () => setLoaded(true) };
}
function MissingBadge() {
  return /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 bottom-3 flex justify-center px-3", children: /* @__PURE__ */ jsx("span", { className: "rounded-full border border-forest-900/10 bg-white/90 px-3 py-1 text-[11px] font-semibold text-ink-soft shadow-sm backdrop-blur", children: "Capture du tableau de bord à ajouter" }) });
}
function Shot({ focus, priority = false, alt, className, src = dashboardSrc }) {
  const { width: width2, height: height2 } = useShotDims();
  const { ref, loaded, onLoad } = useImageLoaded();
  return /* @__PURE__ */ jsxs("div", { className: cn("relative w-full overflow-hidden bg-[#f6f5f2]", className), style: { aspectRatio: `${width2} / ${height2}` }, children: [
    /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 origin-top-left transition-transform duration-[1100ms] ease-out-expo", style: { transform: focus ? focusTransform(focus) : "none" }, children: [
      /* @__PURE__ */ jsx(ShotSkeleton, { className: loaded ? "opacity-0" : "opacity-100" }),
      jsx("img", { ref, src, alt, width:2862, height:1624, loading:priority ? "eager" : "lazy", fetchPriority:priority ? "high" : "auto", decoding:"async", draggable:false, onLoad, className:cn("absolute inset-0 size-full select-none transition-opacity duration-700", loaded ? "opacity-100" : "opacity-0"), style:{objectFit:"contain"} })
    ] }),
    !src && jsx(MissingBadge, {})
  ] });
}
function ShotCrop({ region, label, className }) {
  const { width: width2, height: height2 } = useShotDims();
  const { ref, loaded, onLoad } = useImageLoaded();
  const aspect = region.w * width2 / (region.h * height2);
  return /* @__PURE__ */ jsxs("div", { role: "img", "aria-label": label, className: cn("relative w-full overflow-hidden bg-[#f6f5f2]", className), style: { aspectRatio: String(aspect) }, children: [
    /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: cn("absolute inset-0 animate-shimmer bg-linear-to-r from-forest-50 via-white to-forest-50 transition-opacity duration-700", loaded && "opacity-0") }),
    jsx("img", { ref, src:dashboardSrc, alt:"", "aria-hidden":true, loading:"lazy", decoding:"async", draggable:false, onLoad, className:cn("absolute max-w-none select-none transition-opacity duration-700",loaded ? "opacity-100" : "opacity-0"),style:{width:`${100/region.w}%`,left:`${-region.x/region.w*100}%`,top:`${-region.y/region.h*100}%`} })
  ] });
}
const SHOT_ALT = "Tableau de bord NSARA : indicateurs chiffre d'affaires, ventes enregistrées, produits à suivre et créances clients, liste des dernières ventes et bloc « À garder à l'œil ».";
function ZoomButton() {
  useRef(null);
  const { width: width2, height: height2 } = useShotDims();
  return null;
}
function WindowChrome({ url = "nsara.app/dashboard" }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b border-forest-900/[0.06] bg-forest-50/80 px-3.5 py-2.5 sm:px-4", children: [
    /* @__PURE__ */ jsx("span", { className: "size-2.5 rounded-full bg-forest-900/10", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("span", { className: "size-2.5 rounded-full bg-forest-900/10", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("span", { className: "size-2.5 rounded-full bg-forest-900/10", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("span", { className: "ml-2 min-w-0 truncate rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-ink/65 ring-1 ring-forest-900/[0.06]", children: url }),
    /* @__PURE__ */ jsx("span", { className: "ml-auto shrink-0", children: /* @__PURE__ */ jsx(ZoomButton, {}) })
  ] });
}
function ShotWindow({ children, className, url }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "overflow-hidden rounded-[1.4rem] border border-forest-900/10 bg-white shadow-[0_50px_90px_-40px_rgba(19,33,26,0.45),0_12px_30px_-14px_rgba(19,33,26,0.18)]",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(WindowChrome, { url }),
        children
      ]
    }
  );
}
const line1 = ["Gérez", "votre", "activité."];
const line2 = ["Vendez", "partout."];
const delay = (ms) => ({ "--d": `${ms}ms` });
function FloatCard({
  Icon,
  title,
  text,
  className,
  depth,
  floatClass,
  enterDelay
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("absolute z-20 hidden sm:block", className), children: /* @__PURE__ */ jsx("div", { className: depth, children: /* @__PURE__ */ jsx("div", { className: "enter", style: delay(enterDelay), children: /* @__PURE__ */ jsx("div", { className: floatClass, children: /* @__PURE__ */ jsxs("div", { className: "glass flex items-center gap-3.5 rounded-2xl py-3.5 pl-4 pr-5 shadow-[0_24px_50px_-20px_rgba(19,33,26,0.4)] ring-1 ring-forest-900/[0.06]", children: [
    /* @__PURE__ */ jsx("span", { className: "grid size-10 shrink-0 place-items-center rounded-xl bg-forest-100 text-forest-700", children: /* @__PURE__ */ jsx(Icon, { className: "size-5", strokeWidth: 1.8, "aria-hidden": "true" }) }),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx("p", { className: "font-display text-[15px] font-bold leading-tight text-ink", children: title }),
      /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-[13px] leading-snug text-muted", children: text })
    ] })
  ] }) }) }) }) });
}
function Hero() {
  const stageRef = useRef(null);
  const raf = useRef(0);
  const onMove = (e) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (e.pointerType !== "mouse") return;
    const el = stageRef.current;
    if (!el) return;
    const { clientX, clientY } = e;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const r2 = el.getBoundingClientRect();
      const x = ((clientX - r2.left) / r2.width - 0.5) * 2;
      const y = ((clientY - r2.top) / r2.height - 0.5) * 2;
      el.style.setProperty("--mx", Math.max(-1, Math.min(1, x)).toFixed(3));
      el.style.setProperty("--my", Math.max(-1, Math.min(1, y)).toFixed(3));
    });
  };
  const onLeave = () => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty("--mx", "0");
    el.style.setProperty("--my", "0");
  };
  return /* @__PURE__ */ jsxs("section", { id: "top", "aria-labelledby": "hero-title", className: "relative overflow-x-clip pb-20 pt-32 sm:pt-40 lg:pb-28 lg:pt-44", children: [
    /* @__PURE__ */ jsxs("div", { "aria-hidden": "true", className: "pointer-events-none absolute inset-0 -z-10", children: [
      /* @__PURE__ */ jsx("div", { className: "bg-grid absolute inset-x-0 top-0 h-[780px]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -right-24 -top-20 size-[34rem] animate-drift rounded-full bg-forest-200/60 blur-[90px]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -left-32 top-[28rem] size-[28rem] animate-drift rounded-full bg-gold-100/80 blur-[90px] [animation-delay:-6s] [animation-direction:alternate-reverse]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-paper to-transparent" })
    ] }),
    /* @__PURE__ */ jsxs(Container, { className: "grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8 xl:gap-12", children: [
      /* @__PURE__ */ jsxs("div", { className: "max-w-2xl", children: [
        /* @__PURE__ */ jsx("div", { className: "enter", style: delay(0), children: /* @__PURE__ */ jsx(Eyebrow, { children: "Pensé pour les entrepreneurs africains" }) }),
        /* @__PURE__ */ jsxs(
          "h1",
          {
            id: "hero-title",
            className: "mt-6 font-display text-[2.7rem] font-extrabold leading-[0.96] tracking-[-0.03em] text-ink sm:text-[4rem] lg:text-[3.3rem] xl:text-[4.1rem]",
            children: [
              /* @__PURE__ */ jsx("span", { className: "block", children: line1.map((w, i) => /* @__PURE__ */ jsx("span", { className: "word mr-[0.22em]", style: { "--i": i }, children: w }, w)) }),
              /* @__PURE__ */ jsx("span", { className: "block", children: line2.map((w, i) => /* @__PURE__ */ jsx("span", { className: "word text-gradient mr-[0.22em] pb-[0.08em]", style: { "--i": line1.length + i }, children: w }, w)) })
            ]
          }
        ),
        /* @__PURE__ */ jsx("p", { className: "enter mt-7 max-w-xl text-pretty text-lg leading-relaxed text-ink-soft sm:text-xl", style: delay(450), children: "Ventes, boutiques, inventaire, clients, factures, comptabilité et créances : NSARA réunit l'essentiel pour piloter votre activité simplement." }),
        /* @__PURE__ */ jsxs("div", { className: "enter mt-9 flex flex-col gap-3 sm:flex-row", style: delay(600), children: [
          /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, size: "lg", arrow: true, className: "w-full sm:w-auto", children: "Créer un compte gratuitement" }),
          /* @__PURE__ */ jsx(Button, { href: "#fonctionnalites", variant: "secondary", size: "lg", className: "w-full sm:w-auto", children: "Découvrir NSARA" })
        ] }),
        /* @__PURE__ */ jsxs("p", { className: "enter mt-5 text-[15px] text-ink-soft", style: delay(700), children: [
          "Déjà un compte ?",
          " ",
          /* @__PURE__ */ jsx("a", { href: LOGIN_URL, className: "font-bold text-forest-700 underline decoration-forest-300 decoration-2 underline-offset-4 transition-colors hover:text-forest-900 hover:decoration-forest-600", children: "Connexion" })
        ] }),
        /* @__PURE__ */ jsx("ul", { className: "enter mt-8 flex flex-wrap gap-x-6 gap-y-2.5 text-sm font-medium text-ink-soft", style: delay(800), children: ["Création de compte gratuite", "Interface en français", "Montants en FCFA"].map((t) => /* @__PURE__ */ jsxs("li", { className: "inline-flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "grid size-5 place-items-center rounded-full bg-forest-100 text-forest-700", children: /* @__PURE__ */ jsx(Check, { className: "size-3", strokeWidth: 3, "aria-hidden": "true" }) }),
          t
        ] }, t)) })
      ] }),
      /* @__PURE__ */ jsxs("div", { ref: stageRef, onPointerMove: onMove, onPointerLeave: onLeave, className: "relative mx-auto w-full max-w-[680px] lg:max-w-none xl:-mr-10", children: [
        /* @__PURE__ */ jsxs("div", { className: "enter mb-4 flex flex-wrap items-center gap-x-3 gap-y-1", style: delay(350), children: [
          /* @__PURE__ */ jsx(Eyebrow, { children: "Aperçu du produit" }),
          /* @__PURE__ */ jsx("span", { className: "text-xs font-medium text-muted", children: "Capture réelle · données de démonstration anonymisées" })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "relative pb-6 sm:pb-16", children: [
          /* @__PURE__ */ jsx("div", { className: "enter", style: delay(300), children: /* @__PURE__ */ jsx("div", { className: "tilt", children: /* @__PURE__ */ jsx(ShotWindow, { children: /* @__PURE__ */ jsx(Shot, { priority: true, alt: SHOT_ALT }) }) }) }),
          /* @__PURE__ */ jsx(
            FloatCard,
            {
              Icon: ShoppingBag,
              title: "Dernières ventes",
              text: "Numéro, date, statut et montant",
              depth: "depth-3",
              floatClass: "animate-float-slow [animation-delay:-3s]",
              enterDelay: 950,
              className: "-bottom-9 left-2 lg:-left-6"
            }
          ),
          /* @__PURE__ */ jsx(
            FloatCard,
            {
              Icon: Store,
              title: "Toutes les boutiques",
              text: "Une vue d'ensemble de l'activité",
              depth: "depth-2",
              floatClass: "animate-float",
              enterDelay: 1100,
              className: "-bottom-12 right-2 lg:right-6"
            }
          )
        ] })
      ] })
    ] })
  ] });
}
function useCountUp(target, { duration = 1600, enabled = true } = {}) {
  const [value, setValue] = useState(() => skipMotion() ? target : 0);
  const fromRef = useRef(value);
  useEffect(() => {
    if (!enabled) return;
    if (skipMotion()) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 4);
      const next = from + (target - from) * eased;
      fromRef.current = next;
      setValue(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled]);
  return value;
}
const formatters = /* @__PURE__ */ new Map();
function formatNumber(n, decimals = 0) {
  let f = formatters.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    formatters.set(decimals, f);
  }
  return f.format(n).replace(/[\u202f\u00a0]/g, " ");
}
const nbsp = (s) => s.replace(/^ /, " ");
function CountUp({ value, decimals = 0, prefix = "", suffix = "", duration, className }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const current = useCountUp(value, { enabled: inView, duration });
  return /* @__PURE__ */ jsxs("span", { ref, className: cn("whitespace-nowrap tabular-nums", className), children: [
    /* @__PURE__ */ jsxs("span", { "aria-hidden": "true", children: [
      prefix,
      formatNumber(current, decimals),
      nbsp(suffix)
    ] }),
    /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
      prefix,
      formatNumber(value, decimals),
      nbsp(suffix)
    ] })
  ] });
}
const modules = [
  { Icon: LayoutDashboard, label: "Tableau de bord" },
  { Icon: ShoppingBag, label: "Ventes" },
  { Icon: Store, label: "Boutiques" },
  { Icon: Package, label: "Inventaire" },
  { Icon: Users, label: "Clients" },
  { Icon: FileText, label: "Factures" },
  { Icon: Calculator, label: "Comptabilité" },
  { Icon: Wallet, label: "Créances" },
  { Icon: BookOpen, label: "Bibliothèque" }
];
const stats = [
  { value: 9, label: "rubriques réunies dans un seul menu", hint: "De Tableau de bord à Bibliothèque" },
  { value: 4, label: "indicateurs clés dès l'ouverture", hint: "Chiffre d'affaires, ventes, produits à suivre, créances" },
  { value: 1, label: "sélecteur pour voir toutes vos boutiques", hint: "« Toutes les boutiques », sur le tableau de bord" }
];
function Row({ hidden = false }) {
  return /* @__PURE__ */ jsx("ul", { className: "flex shrink-0 items-center gap-3 pr-3", "aria-hidden": hidden || void 0, children: modules.map(({ Icon, label }) => /* @__PURE__ */ jsxs(
    "li",
    {
      className: "flex items-center gap-2.5 whitespace-nowrap rounded-full border border-forest-900/[0.08] bg-white px-4 py-2.5 text-sm font-semibold text-ink/80 shadow-[0_1px_2px_rgba(19,33,26,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-forest-400/50 hover:text-forest-800 hover:shadow-md",
      children: [
        /* @__PURE__ */ jsx(Icon, { className: "size-4 text-forest-600", strokeWidth: 1.8, "aria-hidden": "true" }),
        label
      ]
    },
    label
  )) });
}
function SocialProof() {
  return /* @__PURE__ */ jsxs("section", { "aria-labelledby": "proof-title", className: "relative py-14 sm:py-20", children: [
    /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsx(Reveal, { className: "text-center", children: /* @__PURE__ */ jsx("h2", { id: "proof-title", className: "text-xs font-bold uppercase tracking-[0.2em] text-muted sm:text-[0.8rem]", children: "Tout ce qu'il vous faut, dans votre menu" }) }) }),
    /* @__PURE__ */ jsx(Reveal, { delay: 100, className: "fade-x group mt-8 overflow-hidden", children: /* @__PURE__ */ jsxs("div", { className: "flex w-max animate-marquee group-hover:[animation-play-state:paused]", children: [
      /* @__PURE__ */ jsx(Row, {}),
      /* @__PURE__ */ jsx(Row, { hidden: true })
    ] }) }),
    /* @__PURE__ */ jsx(Container, { className: "mt-14 sm:mt-20", children: /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-1 gap-px overflow-hidden rounded-3xl border border-forest-900/[0.08] bg-forest-900/[0.08] shadow-[0_1px_2px_rgba(19,33,26,0.04)] sm:grid-cols-3", children: stats.map((s, i) => /* @__PURE__ */ jsxs(Reveal, { delay: i * 90, className: "flex flex-col bg-white p-6 sm:p-8", children: [
      /* @__PURE__ */ jsx("dt", { className: "order-2 mt-2 text-[15px] leading-snug text-ink-soft", children: s.label }),
      /* @__PURE__ */ jsx("dd", { className: "order-1 font-display text-5xl font-extrabold tracking-[-0.03em] text-ink sm:text-6xl", children: /* @__PURE__ */ jsx(CountUp, { value: s.value, duration: 1200 + i * 150 }) }),
      /* @__PURE__ */ jsx("dd", { className: "order-3 mt-3 text-xs font-medium text-forest-600", children: s.hint })
    ] }, s.label)) }) })
  ] });
}
function handlePointerGlow(e) {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty("--x", `${e.clientX - rect.left}px`);
  el.style.setProperty("--y", `${e.clientY - rect.top}px`);
}
function FeatureCard({
  Icon,
  title,
  text,
  className,
  delay: delay2 = 0,
  split = false,
  children
}) {
  return /* @__PURE__ */ jsx(Reveal, { as: "article", delay: delay2, className: cn("flex", className), children: /* @__PURE__ */ jsxs(
    "div",
    {
      onPointerMove: handlePointerGlow,
      className: cn(
        "spotlight group relative flex w-full flex-col overflow-hidden rounded-[1.75rem] border border-forest-900/[0.08] bg-white p-6 shadow-[0_1px_2px_rgba(19,33,26,0.04)] transition-all duration-500 ease-out-expo hover:-translate-y-1 hover:border-forest-900/15 hover:shadow-[0_34px_60px_-34px_rgba(19,33,26,0.3)] sm:p-8",
        split && "md:flex-row md:items-center md:gap-8"
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: split ? "md:flex-1" : void 0, children: [
          /* @__PURE__ */ jsx("span", { className: "grid size-11 place-items-center rounded-2xl bg-forest-100 text-forest-700 transition-all duration-500 ease-out-expo group-hover:rotate-[-6deg] group-hover:scale-110 group-hover:bg-forest-700 group-hover:text-white", children: /* @__PURE__ */ jsx(Icon, { className: "size-5", strokeWidth: 1.8, "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsx("h3", { className: "mt-5 font-display text-2xl font-bold tracking-[-0.02em] text-ink", children: title }),
          /* @__PURE__ */ jsx("p", { className: "mt-2 max-w-md text-[15px] leading-relaxed text-ink-soft", children: text })
        ] }),
        /* @__PURE__ */ jsx("div", { className: split ? "mt-7 md:mt-0 md:w-[15.5rem] md:shrink-0" : "mt-7 flex flex-1 flex-col justify-end sm:mt-8", children })
      ]
    }
  ) });
}
function Snippet({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "overflow-hidden rounded-2xl border border-forest-900/10 bg-white shadow-[0_18px_40px_-26px_rgba(19,33,26,0.45)] transition-transform duration-500 ease-out-expo group-hover:-translate-y-1", children });
}
const facts = [
  { Icon: Wallet, title: "Montants en FCFA", text: "Vos chiffres s'affichent dans votre monnaie." },
  { Icon: Languages, title: "Interface en français", text: "Des intitulés simples : Ventes, Inventaire, Clients…" },
  { Icon: PanelLeft, title: "Un menu clair", text: "Quatre groupes de rubriques, et un menu que vous pouvez réduire." },
  { Icon: LayoutDashboard, title: "Une vue d'ensemble", text: "Votre activité, en un coup d'œil dès l'ouverture." }
];
function Features() {
  return /* @__PURE__ */ jsx("section", { id: "fonctionnalites", "aria-labelledby": "features-title", className: "relative py-20 sm:py-28 lg:py-32", children: /* @__PURE__ */ jsxs(Container, { children: [
    /* @__PURE__ */ jsx(
      SectionHeading,
      {
        titleId: "features-title",
        eyebrow: "Fonctionnalités",
        title: /* @__PURE__ */ jsxs(Fragment, { children: [
          "Tout pour piloter votre activité. ",
          /* @__PURE__ */ jsx("span", { className: "text-gradient", children: "Rien de superflu." })
        ] }),
        description: "NSARA rassemble vos ventes, vos boutiques, votre inventaire, vos clients et vos finances dans un espace clair. Les extraits ci-dessous viennent de l'application."
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "mt-14 grid gap-4 sm:mt-20 sm:gap-5 md:grid-cols-2 lg:grid-cols-12", children: [
      /* @__PURE__ */ jsx(
        FeatureCard,
        {
          Icon: ShoppingBag,
          title: "Suivi des ventes",
          text: "Chaque vente apparaît dans vos dernières ventes avec son numéro, sa date, son statut et son montant en FCFA. Un bouton « Nouvelle vente » reste à portée de main.",
          className: "md:col-span-2 lg:col-span-7",
          children: /* @__PURE__ */ jsx(Snippet, { children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.sales, label: "Extrait de l'application : liste des dernières ventes, avec numéro, date, statut Payée et montant en FCFA." }) })
        }
      ),
      /* @__PURE__ */ jsx(
        FeatureCard,
        {
          Icon: Package,
          title: "Stock et inventaire",
          text: "Repérez les produits à suivre et ceux en stock faible. Le bloc « À garder à l'œil » affiche aussi vos encaissements, le nombre de clients, la marge brute et la valeur du stock.",
          className: "md:col-span-2 lg:col-span-5",
          delay: 90,
          children: /* @__PURE__ */ jsx(Snippet, { children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.watch, label: "Extrait de l'application : bloc « À garder à l'œil » avec votre stock, vos encaissements, le nombre de clients, la marge brute et la valeur du stock." }) })
        }
      ),
      /* @__PURE__ */ jsx(
        FeatureCard,
        {
          Icon: Store,
          title: "Plusieurs boutiques",
          text: "Une rubrique Boutiques et un sélecteur « Toutes les boutiques » pour voir l'ensemble de votre activité.",
          className: "md:col-span-1 lg:col-span-6",
          split: true,
          children: /* @__PURE__ */ jsx(Snippet, { children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.actions, label: "Extrait de l'application : bouton Nouvelle vente et sélecteur « Toutes les boutiques »." }) })
        }
      ),
      /* @__PURE__ */ jsx(
        FeatureCard,
        {
          Icon: Wallet,
          title: "Créances clients",
          text: "Voyez d'un coup d'œil le montant qu'il vous reste à recevoir de vos clients.",
          className: "md:col-span-1 lg:col-span-6",
          delay: 90,
          split: true,
          children: /* @__PURE__ */ jsx(Snippet, { children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.debts, label: "Extrait de l'application : indicateur « Créances clients », montant restant à recevoir." }) })
        }
      ),
      /* @__PURE__ */ jsx(Reveal, { as: "article", className: "md:col-span-2 lg:col-span-12", children: /* @__PURE__ */ jsxs(
        "div",
        {
          onPointerMove: handlePointerGlow,
          className: "spotlight group grid items-center gap-8 overflow-hidden rounded-[1.75rem] border border-forest-900/[0.08] bg-white p-6 shadow-[0_1px_2px_rgba(19,33,26,0.04)] transition-all duration-500 ease-out-expo hover:-translate-y-1 hover:shadow-[0_34px_60px_-34px_rgba(19,33,26,0.3)] sm:p-8 md:grid-cols-[1.4fr_1fr] lg:gap-14 lg:p-10",
          children: [
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("span", { className: "grid size-11 place-items-center rounded-2xl bg-forest-100 text-forest-700 transition-all duration-500 ease-out-expo group-hover:rotate-[-6deg] group-hover:scale-110 group-hover:bg-forest-700 group-hover:text-white", children: /* @__PURE__ */ jsx(FileText, { className: "size-5", strokeWidth: 1.8, "aria-hidden": "true" }) }),
              /* @__PURE__ */ jsx("h3", { className: "mt-5 font-display text-2xl font-bold tracking-[-0.02em] sm:text-3xl", children: "Vos finances, dans une rubrique dédiée" }),
              /* @__PURE__ */ jsx("p", { className: "mt-3 max-w-lg text-[15px] leading-relaxed text-ink-soft sm:text-base", children: "Factures, comptabilité et créances sont regroupées dans la rubrique « Vos finances » du menu, à côté de votre activité." }),
              /* @__PURE__ */ jsx("ul", { className: "mt-6 flex flex-wrap gap-2", children: ["Factures", "Comptabilité", "Créances"].map((t) => /* @__PURE__ */ jsx("li", { className: "rounded-full bg-forest-100 px-3.5 py-1.5 text-sm font-semibold text-forest-700", children: t }, t)) })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "mx-auto w-full max-w-sm overflow-hidden rounded-2xl border border-forest-900/10 shadow-[0_24px_50px_-26px_rgba(19,33,26,0.55)] transition-transform duration-500 ease-out-expo group-hover:-translate-y-1 md:mx-0 md:ml-auto", children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.finances, label: "Extrait du menu de l'application : rubrique « Vos finances » avec Factures, Comptabilité et Créances." }) })
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx("ul", { className: "mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4", children: facts.map(({ Icon, title, text }, i) => /* @__PURE__ */ jsxs(Reveal, { as: "li", delay: i * 80, className: "flex items-start gap-4 rounded-2xl p-4 transition-colors duration-300 hover:bg-white sm:p-5", children: [
      /* @__PURE__ */ jsx("span", { className: "grid size-10 shrink-0 place-items-center rounded-xl bg-forest-100 text-forest-700", children: /* @__PURE__ */ jsx(Icon, { className: "size-[18px]", strokeWidth: 1.8, "aria-hidden": "true" }) }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "font-display text-base font-bold tracking-tight", children: title }),
        /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm leading-relaxed text-ink-soft", children: text })
      ] })
    ] }, title)) })
  ] }) });
}
const tabs = [
  {
    id: "overview",
    label: "Tableau de bord",
    src: dashboardSrc,
    url: "nsara.app/dashboard",
    Icon: LayoutDashboard,
    text: "Votre activité, en un coup d'œil : vos indicateurs, vos dernières ventes et ce qu'il faut garder à l'œil, avec « Nouvelle vente » toujours visible.",
    points: ["Bouton « Nouvelle vente »", "Sélecteur « Toutes les boutiques »"],
    focus: focuses.overview,
    alt: "Tableau de bord NSARA en vue d'ensemble : indicateurs, dernières ventes et bloc « À garder à l'œil »."
  },
  {
    id: "facturation",
    label: "Facturation",
    src: "/assets/screens/facturation.webp",
    url: "nsara.app/facture",
    Icon: ChartColumn,
    text: "Créez vos factures et suivez leur statut, de « Brouillon » à « Payée », avec le montant qu’il vous reste à recevoir.",
    points: ["Recherche par numéro, client ou montant", "Filtres par statut et par période"],
    focus: focuses.overview,
    alt: "Facturation NSARA — capture réelle anonymisée de données de démonstration."
  },
  {
    id: "boutiques",
    label: "Boutiques",
    src: "/assets/screens/boutiques.webp",
    url: "nsara.app/boutique",
    Icon: ShoppingBag,
    text: "Gérez vos boutiques depuis un seul espace et ouvrez leur vitrine en ligne d’un clic.",
    points: ["Bouton « Créer une boutique »", "Lien « Voir la boutique »"],
    focus: focuses.overview,
    alt: "Boutiques NSARA — capture réelle anonymisée de données de démonstration."
  },
  {
    id: "inventaire",
    label: "Inventaire",
    src: "/assets/screens/inventaire.webp",
    url: "nsara.app/inventaire",
    Icon: Package,
    text: "Suivez vos produits, la valeur d’achat de votre stock et son potentiel de revenus, pour toutes vos boutiques.",
    points: ["Vue en grille ou en liste", "Filtre « Toutes les boutiques »"],
    focus: focuses.overview,
    alt: "Inventaire NSARA — état vide réel, compte anonymisé."
  }
];
const AUTOPLAY_MS = 7e3;
function Showcase() {
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(() => !skipMotion());
  const [paused, setPaused] = useState(false);
  const [sectionRef, inView] = useInView({ threshold: 0.3 });
  const tabRefs = useRef([]);
  const select = useCallback((i, byUser = true) => {
    setActive(i);
    if (byUser) setAutoplay(false);
  }, []);
  const onKeyDown = (e) => {
    const last = tabs.length - 1;
    let next = -1;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = active === last ? 0 : active + 1;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = active === 0 ? last : active - 1;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = last;
    if (next >= 0) {
      e.preventDefault();
      select(next);
      tabRefs.current[next]?.focus();
    }
  };
  const current = tabs[active];
  const running = autoplay && inView;
  return /* @__PURE__ */ jsx("section", { id: "solutions", "aria-labelledby": "solutions-title", className: "relative bg-linear-to-b from-transparent via-forest-50/80 to-transparent py-20 sm:py-28 lg:py-32", children: /* @__PURE__ */ jsxs(Container, { children: [
    /* @__PURE__ */ jsx(
      SectionHeading,
      {
        titleId: "solutions-title",
        eyebrow: "Solutions",
        title: /* @__PURE__ */ jsxs(Fragment, { children: [
          "Un seul espace, ",
          /* @__PURE__ */ jsx("span", { className: "text-gradient", children: "de la vente à l'encaissement." })
        ] }),
        description: "Découvrez de vraies captures de NSARA, réalisées avec des données de démonstration."
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: sectionRef,
        onPointerEnter: () => setPaused(true),
        onPointerLeave: () => setPaused(false),
        onFocusCapture: () => setPaused(true),
        onBlurCapture: () => setPaused(false),
        className: "mt-14 grid items-start gap-8 sm:mt-20 lg:grid-cols-[0.78fr_1.22fr] lg:gap-12",
        children: [
          /* @__PURE__ */ jsx(Reveal, { variant: "left", children: /* @__PURE__ */ jsx("div", { role: "tablist", "aria-label": "Zones du tableau de bord", "aria-orientation": "vertical", onKeyDown, className: "flex flex-col gap-2", children: tabs.map((t, i) => {
            const isActive = i === active;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                ref: (el) => {
                  tabRefs.current[i] = el;
                },
                role: "tab",
                type: "button",
                id: `tab-${t.id}`,
                "aria-selected": isActive,
                "aria-controls": "showcase-panel",
                tabIndex: isActive ? 0 : -1,
                onClick: () => select(i),
                className: cn(
                  "relative w-full overflow-hidden rounded-2xl border p-4 text-left transition-all duration-500 ease-out-expo sm:p-5",
                  isActive ? "border-forest-900/10 bg-white shadow-[0_24px_50px_-30px_rgba(19,33,26,0.35)]" : "border-transparent hover:bg-white/70"
                ),
                children: [
                  /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-3.5", children: [
                    /* @__PURE__ */ jsx("span", { className: cn("grid size-10 shrink-0 place-items-center rounded-xl transition-all duration-500", isActive ? "bg-forest-700 text-white" : "bg-forest-100 text-forest-700"), children: /* @__PURE__ */ jsx(t.Icon, { className: "size-[18px]", strokeWidth: 1.8, "aria-hidden": "true" }) }),
                    /* @__PURE__ */ jsx("span", { className: cn("font-display text-lg font-bold tracking-tight transition-colors sm:text-xl", isActive ? "text-ink" : "text-ink/70"), children: t.label })
                  ] }),
                  /* @__PURE__ */ jsx("span", { className: cn("grid transition-[grid-template-rows,opacity] duration-500 ease-out-expo", isActive ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"), children: /* @__PURE__ */ jsxs("span", { className: "overflow-hidden", children: [
                    /* @__PURE__ */ jsx("span", { className: "mt-3 block pl-[3.4rem] text-[15px] leading-relaxed text-ink-soft", children: t.text }),
                    /* @__PURE__ */ jsx("span", { className: "mt-3 block space-y-1.5 pb-1 pl-[3.4rem]", children: t.points.map((p) => /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2 text-[13px] font-medium text-forest-700", children: [
                      /* @__PURE__ */ jsx(Check, { className: "size-3.5", strokeWidth: 3, "aria-hidden": "true" }),
                      p
                    ] }, p)) })
                  ] }) }),
                  isActive && running && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "absolute inset-x-0 bottom-0 h-0.5 bg-forest-100", children: /* @__PURE__ */ jsx(
                    "span",
                    {
                      onAnimationEnd: () => select((active + 1) % tabs.length, false),
                      className: "block h-full origin-left bg-forest-500",
                      style: { animation: `nsara-premium-progress ${AUTOPLAY_MS}ms linear forwards`, animationPlayState: paused ? "paused" : "running" }
                    },
                    `${active}-progress`
                  ) })
                ]
              },
              t.id
            );
          }) }) }),
          /* @__PURE__ */ jsx(Reveal, { variant: "right", delay: 120, children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "absolute -inset-4 -z-10 rounded-[2.2rem] bg-linear-to-br from-forest-200/60 via-transparent to-gold-100/70 blur-2xl" }),
            /* @__PURE__ */ jsx(ShotWindow, { url:current.url, children: /* @__PURE__ */ jsx("div", { id: "showcase-panel", role: "tabpanel", "aria-labelledby": `tab-${current.id}`, tabIndex: 0, children: /* @__PURE__ */ jsx(Shot, { src:current.src, focus: current.focus, alt: current.alt }, current.id) }) }),
            null
          ] }) })
        ]
      }
    )
  ] }) });
}
const benefits = [
  {
    Icon: ChartColumn,
    title: "Des chiffres clairs dès l'ouverture",
    text: "Chiffre d'affaires, ventes enregistrées, panier moyen et marge brute : l'essentiel de votre activité s'affiche sur votre tableau de bord.",
    tag: "Tableau de bord"
  },
  {
    Icon: PackageCheck,
    title: "Un stock que vous gardez à l'œil",
    text: "Les produits à suivre et ceux en stock faible sont signalés, et vous connaissez la valeur de votre stock.",
    tag: "Produits à suivre"
  },
  {
    Icon: Wallet,
    title: "Savoir ce qu'on vous doit",
    text: "Vos créances clients et vos encaissements ont leur place dans le menu : le montant restant à recevoir est visible tout de suite.",
    tag: "Créances suivies"
  },
  {
    Icon: Store,
    title: "Toutes vos boutiques, une seule vue",
    text: "Un sélecteur « Toutes les boutiques » vous donne la vue d'ensemble de votre activité, sans jongler entre plusieurs outils.",
    tag: "Vue d'ensemble"
  }
];
const before = ["Ventes notées dans un carnet", "Stock compté à la main", "Chiffres éparpillés entre plusieurs outils", "Créances à retenir de tête"];
const after = ["Chaque vente avec son numéro, sa date et son statut", "Votre stock et sa valeur sous les yeux", "Vos créances clients à portée de clic", "Toutes vos boutiques dans un même espace"];
function Benefits() {
  return /* @__PURE__ */ jsxs("section", { id: "avantages", "aria-labelledby": "benefits-title", className: "grain relative isolate overflow-hidden bg-forest-950 py-20 text-white sm:py-28 lg:py-32", children: [
    /* @__PURE__ */ jsxs("div", { "aria-hidden": "true", className: "pointer-events-none absolute inset-0 -z-10", children: [
      /* @__PURE__ */ jsx("div", { className: "bg-grid-dark absolute inset-0" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -left-40 top-0 size-[36rem] animate-drift rounded-full bg-forest-600/30 blur-[110px]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -right-32 bottom-0 size-[32rem] animate-drift rounded-full bg-gold-500/15 blur-[110px] [animation-delay:-8s]" })
    ] }),
    /* @__PURE__ */ jsxs(Container, { children: [
      /* @__PURE__ */ jsx(
        SectionHeading,
        {
          tone: "dark",
          titleId: "benefits-title",
          eyebrow: "Pourquoi NSARA",
          title: /* @__PURE__ */ jsxs(Fragment, { children: [
            "Une activité claire. ",
            /* @__PURE__ */ jsx("span", { className: "text-gradient-light", children: "Des décisions plus simples." })
          ] }),
          description: "NSARA vous donne une vision nette de vos ventes, de votre stock et de ce qu'on vous doit, pour piloter sans vous perdre dans les détails."
        }
      ),
      /* @__PURE__ */ jsx("ul", { className: "mt-14 grid gap-4 sm:mt-20 sm:grid-cols-2 lg:grid-cols-4", children: benefits.map(({ Icon, title, text, tag }, i) => /* @__PURE__ */ jsx(Reveal, { as: "li", delay: i * 100, className: "flex", children: /* @__PURE__ */ jsxs(
        "div",
        {
          onPointerMove: handlePointerGlow,
          className: "spotlight spotlight-dark glass-dark group flex w-full flex-col rounded-3xl p-6 transition-all duration-500 ease-out-expo hover:-translate-y-1.5 hover:border-white/20 hover:bg-white/[0.07] sm:p-7",
          children: [
            /* @__PURE__ */ jsx("span", { className: "grid size-12 place-items-center rounded-2xl bg-white/10 text-forest-200 ring-1 ring-white/10 transition-all duration-500 ease-out-expo group-hover:scale-110 group-hover:bg-forest-300 group-hover:text-forest-950", children: /* @__PURE__ */ jsx(Icon, { className: "size-5", strokeWidth: 1.8, "aria-hidden": "true" }) }),
            /* @__PURE__ */ jsx("h3", { className: "mt-6 font-display text-xl font-bold leading-snug tracking-[-0.02em]", children: title }),
            /* @__PURE__ */ jsx("p", { className: "mt-3 flex-1 text-[15px] leading-relaxed text-forest-200/75", children: text }),
            /* @__PURE__ */ jsxs("span", { className: "mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white/[0.07] px-3 py-1 text-xs font-semibold text-forest-200", children: [
              /* @__PURE__ */ jsx(Check, { className: "size-3.5", strokeWidth: 3, "aria-hidden": "true" }),
              tag
            ] })
          ]
        }
      ) }, title)) }),
      /* @__PURE__ */ jsxs("div", { className: "mt-16 grid gap-4 sm:mt-24 md:grid-cols-2 md:gap-6", children: [
        /* @__PURE__ */ jsx(Reveal, { variant: "left", children: /* @__PURE__ */ jsxs("div", { className: "h-full rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-9", children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-bold uppercase tracking-[0.18em] text-forest-300/70", children: "Sans NSARA" }),
          /* @__PURE__ */ jsx("h3", { className: "mt-3 font-display text-2xl font-bold tracking-[-0.02em] text-white/90 sm:text-3xl", children: "Des outils dispersés" }),
          /* @__PURE__ */ jsx("ul", { className: "mt-7 space-y-4", children: before.map((t, i) => /* @__PURE__ */ jsxs(Reveal, { as: "li", delay: 150 + i * 80, className: "flex items-center gap-3.5 text-[15px] text-forest-200/70", children: [
            /* @__PURE__ */ jsx("span", { className: "grid size-6 shrink-0 place-items-center rounded-full bg-white/[0.07] text-forest-300/70", children: /* @__PURE__ */ jsx(X, { className: "size-3.5", strokeWidth: 2.6, "aria-hidden": "true" }) }),
            t
          ] }, t)) })
        ] }) }),
        /* @__PURE__ */ jsx(Reveal, { variant: "right", delay: 100, children: /* @__PURE__ */ jsxs("div", { className: "relative h-full overflow-hidden rounded-3xl border border-forest-300/30 bg-linear-to-br from-forest-700/90 to-forest-800/90 p-6 shadow-[0_40px_80px_-40px_rgba(120,166,143,0.5)] sm:p-9", children: [
          /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "absolute -right-16 -top-16 size-56 animate-drift rounded-full bg-forest-300/20 blur-3xl" }),
          /* @__PURE__ */ jsx("p", { className: "relative text-xs font-bold uppercase tracking-[0.18em] text-forest-200", children: "Avec NSARA" }),
          /* @__PURE__ */ jsx("h3", { className: "relative mt-3 font-display text-2xl font-bold tracking-[-0.02em] sm:text-3xl", children: "Un espace, une vision claire" }),
          /* @__PURE__ */ jsx("ul", { className: "relative mt-7 space-y-4", children: after.map((t, i) => /* @__PURE__ */ jsxs(Reveal, { as: "li", delay: 250 + i * 80, className: "flex items-center gap-3.5 text-[15px] font-medium text-white", children: [
            /* @__PURE__ */ jsx("span", { className: "grid size-6 shrink-0 place-items-center rounded-full bg-forest-300 text-forest-950", children: /* @__PURE__ */ jsx(Check, { className: "size-3.5", strokeWidth: 3, "aria-hidden": "true" }) }),
            t
          ] }, t)) })
        ] }) })
      ] })
    ] })
  ] });
}
const steps = [
  { title: "Créez votre compte", text: "La création de votre compte est gratuite. Vous accédez ensuite à votre espace de gestion." },
  { title: "Ouvrez votre tableau de bord", text: "Vous y retrouvez les essentiels de votre activité : chiffre d'affaires, ventes enregistrées, produits à suivre et créances clients." },
  { title: "Enregistrez votre première vente", text: "Le bouton « Nouvelle vente » est disponible dès le tableau de bord. Vos ventes s'affichent ensuite dans la liste des dernières ventes." }
];
const menu = [
  { group: "Vue d'ensemble", items: ["Tableau de bord"] },
  { group: "Votre activité", items: ["Ventes", "Boutiques", "Inventaire", "Clients"] },
  { group: "Vos finances", items: ["Factures", "Comptabilité", "Créances"] },
  { group: "Ressources", items: ["Bibliothèque"] }
];
function Support() {
  return /* @__PURE__ */ jsx("section", { id: "accompagnement", "aria-labelledby": "support-title", className: "relative py-20 sm:py-28 lg:py-32", children: /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsxs("div", { className: "grid gap-14 lg:grid-cols-2 lg:gap-20", children: [
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(
        SectionHeading,
        {
          align: "left",
          titleId: "support-title",
          eyebrow: "Accompagnement",
          title: /* @__PURE__ */ jsxs(Fragment, { children: [
            "Démarrez en ",
            /* @__PURE__ */ jsx("span", { className: "text-gradient", children: "3 étapes simples." })
          ] }),
          description: "Un espace pensé pour aller à l'essentiel : un menu lisible, un tableau de bord qui s'ouvre sur votre activité."
        }
      ),
      /* @__PURE__ */ jsx("ol", { className: "mt-12 space-y-2", children: steps.map((s, i) => /* @__PURE__ */ jsxs(Reveal, { as: "li", delay: i * 120, className: "group relative flex gap-5 pb-8 last:pb-0 sm:gap-6", children: [
        i < steps.length - 1 && /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "absolute left-[1.35rem] top-14 bottom-1 w-px bg-linear-to-b from-forest-300 to-forest-100 sm:left-6" }),
        /* @__PURE__ */ jsx("span", { className: "relative z-10 grid size-11 shrink-0 place-items-center rounded-2xl bg-forest-800 font-display text-lg font-extrabold text-white shadow-[0_12px_24px_-10px_rgba(31,61,48,0.7)] transition-transform duration-500 ease-out-expo group-hover:scale-110 group-hover:rotate-[-6deg] sm:size-12", children: i + 1 }),
        /* @__PURE__ */ jsxs("div", { className: "pt-1", children: [
          /* @__PURE__ */ jsx("h3", { className: "font-display text-xl font-bold tracking-[-0.02em] sm:text-2xl", children: s.title }),
          /* @__PURE__ */ jsx("p", { className: "mt-2 max-w-md text-[15px] leading-relaxed text-ink-soft sm:text-base", children: s.text })
        ] })
      ] }, s.title)) }),
      /* @__PURE__ */ jsx(Reveal, { delay: 200, className: "mt-10", children: /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, size: "lg", arrow: true, className: "w-full sm:w-auto", children: "Créer un compte gratuitement" }) })
    ] }),
    /* @__PURE__ */ jsx(Reveal, { variant: "right", className: "lg:pt-4", children: /* @__PURE__ */ jsxs(
      "div",
      {
        onPointerMove: handlePointerGlow,
        className: "spotlight rounded-[1.75rem] border border-forest-900/[0.08] bg-white p-5 shadow-[0_1px_2px_rgba(19,33,26,0.04)] sm:p-8",
        children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-bold uppercase tracking-[0.18em] text-muted", children: "Votre menu" }),
          /* @__PURE__ */ jsx("h3", { className: "mt-2 font-display text-2xl font-bold tracking-[-0.02em] sm:text-3xl", children: "Quatre groupes, tout au même endroit" }),
          /* @__PURE__ */ jsxs("div", { className: "mt-7 grid items-center gap-7 sm:grid-cols-[minmax(0,13rem)_1fr]", children: [
            /* @__PURE__ */ jsx("div", { className: "mx-auto w-full max-w-[13rem] overflow-hidden rounded-2xl border border-forest-900/10 shadow-[0_28px_50px_-26px_rgba(19,33,26,0.6)] sm:mx-0", children: /* @__PURE__ */ jsx(ShotCrop, { region: regions.sidebar, label: "Menu de l'application NSARA : Tableau de bord, Ventes, Boutiques, Inventaire, Clients, Factures, Comptabilité, Créances et Bibliothèque." }) }),
            /* @__PURE__ */ jsx("ul", { className: "space-y-5", children: menu.map((m) => /* @__PURE__ */ jsxs("li", { children: [
              /* @__PURE__ */ jsx("p", { className: "text-[11px] font-bold uppercase tracking-[0.16em] text-forest-600", children: m.group }),
              /* @__PURE__ */ jsx("ul", { className: "mt-2 flex flex-wrap gap-1.5", children: m.items.map((it) => /* @__PURE__ */ jsx("li", { className: "rounded-lg bg-forest-50 px-2.5 py-1 text-sm font-semibold text-ink/80 ring-1 ring-forest-900/[0.06]", children: it }, it)) })
            ] }, m.group)) })
          ] })
        ]
      }
    ) })
  ] }) }) });
}
const faqs = [
  {
    q: "Qu'est-ce que NSARA ?",
    a: "NSARA est un espace de gestion pensé pour les entrepreneurs africains. Il réunit vos ventes, vos boutiques, votre inventaire, vos clients, vos factures, votre comptabilité et vos créances, avec un tableau de bord qui présente votre activité en un coup d'œil."
  },
  {
    q: "La création de compte est-elle gratuite ?",
    a: "Oui, vous pouvez créer votre compte gratuitement."
  },
  {
    q: "Que vois-je en ouvrant mon tableau de bord ?",
    a: "Quatre indicateurs : le chiffre d'affaires (montant total des ventes), les ventes enregistrées (avec le panier moyen), les produits à suivre (dont ceux en stock faible) et les créances clients (montant restant à recevoir). Vous y trouvez aussi vos dernières ventes et un bloc « À garder à l'œil » : stock, encaissements, nombre de clients, marge brute et valeur du stock."
  },
  {
    q: "Puis-je suivre plusieurs boutiques ?",
    a: "NSARA propose une rubrique « Boutiques » et un sélecteur « Toutes les boutiques » sur le tableau de bord, pour avoir une vue d'ensemble de votre activité."
  },
  {
    q: "Comment enregistrer une vente ?",
    a: "Depuis le tableau de bord, utilisez le bouton « Nouvelle vente ». Vos ventes apparaissent ensuite dans la liste des dernières ventes, avec leur numéro, leur date, leur statut et leur montant."
  },
  {
    q: "Où retrouver mes factures, ma comptabilité et mes créances ?",
    a: "Dans le menu, la rubrique « Vos finances » regroupe Factures, Comptabilité et Créances. Vous y suivez vos factures et leur statut de paiement, ainsi que le montant qu'il vous reste à recevoir de vos clients."
  },
  {
    q: "Dans quelle monnaie sont affichés les montants ?",
    a: "Les montants sont affichés en FCFA."
  }
];
const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } }))
};
function FAQ() {
  const [open, setOpen] = useState(0);
  return /* @__PURE__ */ jsxs("section", { id: "faq", "aria-labelledby": "faq-title", className: "relative py-20 sm:py-28 lg:py-32", children: [
    /* @__PURE__ */ jsx("script", { type: "application/ld+json", dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) } }),
    /* @__PURE__ */ jsx(Container, { children: /* @__PURE__ */ jsxs("div", { className: "grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20", children: [
      /* @__PURE__ */ jsxs("div", { className: "lg:sticky lg:top-32 lg:self-start", children: [
        /* @__PURE__ */ jsx(
          SectionHeading,
          {
            align: "left",
            titleId: "faq-title",
            eyebrow: "FAQ",
            title: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Vos questions, ",
              /* @__PURE__ */ jsx("span", { className: "text-gradient", children: "nos réponses." })
            ] }),
            description: "Tout ce qu'il faut savoir avant de créer votre compte."
          }
        ),
        /* @__PURE__ */ jsx(Reveal, { delay: 200, className: "mt-8", children: /* @__PURE__ */ jsxs("div", { className: "rounded-2xl border border-forest-900/[0.08] bg-white p-5 shadow-[0_1px_2px_rgba(19,33,26,0.04)] sm:p-6", children: [
          /* @__PURE__ */ jsx("p", { className: "font-display text-lg font-bold tracking-tight", children: "Une autre question ?" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 text-sm leading-relaxed text-ink-soft", children: "Écrivez-nous, nous vous répondrons." }),
          /* @__PURE__ */ jsxs(
            "a",
            {
              href: `mailto:${CONTACT_EMAIL}`,
              className: "group mt-4 inline-flex items-center gap-2 text-sm font-bold text-forest-700 transition-colors hover:text-forest-900",
              children: [
                /* @__PURE__ */ jsx("span", { className: "grid size-8 place-items-center rounded-lg bg-forest-100 transition-colors group-hover:bg-forest-700 group-hover:text-white", children: /* @__PURE__ */ jsx(Mail, { className: "size-4", "aria-hidden": "true" }) }),
                CONTACT_EMAIL
              ]
            }
          )
        ] }) })
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "space-y-3", children: faqs.map((f, i) => {
        const isOpen = open === i;
        return /* @__PURE__ */ jsx(Reveal, { as: "li", delay: Math.min(i, 4) * 70, children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "overflow-hidden rounded-2xl border bg-white transition-all duration-500 ease-out-expo",
              isOpen ? "border-forest-600/30 shadow-[0_24px_50px_-32px_rgba(19,33,26,0.35)]" : "border-forest-900/[0.08] hover:border-forest-900/20"
            ),
            children: [
              /* @__PURE__ */ jsx("h3", { children: /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  id: `faq-q-${i}`,
                  "aria-expanded": isOpen,
                  "aria-controls": `faq-a-${i}`,
                  onClick: () => setOpen(isOpen ? null : i),
                  className: "flex w-full items-center justify-between gap-4 px-5 py-5 text-left sm:px-7 sm:py-6",
                  children: [
                    /* @__PURE__ */ jsx("span", { className: "font-display text-[1.05rem] font-bold tracking-[-0.015em] sm:text-lg", children: f.q }),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        "aria-hidden": "true",
                        className: cn(
                          "grid size-8 shrink-0 place-items-center rounded-full transition-all duration-500 ease-out-expo",
                          isOpen ? "rotate-[135deg] bg-forest-700 text-white" : "bg-forest-100 text-forest-700"
                        ),
                        children: /* @__PURE__ */ jsx(Plus, { className: "size-4", strokeWidth: 2.4 })
                      }
                    )
                  ]
                }
              ) }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  id: `faq-a-${i}`,
                  role: "region",
                  "aria-labelledby": `faq-q-${i}`,
                  inert: !isOpen ? "" : void 0,
                  className: cn("grid transition-[grid-template-rows,opacity] duration-500 ease-out-expo", isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"),
                  children: /* @__PURE__ */ jsx("div", { className: "overflow-hidden", children: /* @__PURE__ */ jsx("p", { className: "px-5 pb-6 text-[15px] leading-relaxed text-ink-soft sm:px-7 sm:text-base", children: f.a }) })
                }
              )
            ]
          }
        ) }, f.q);
      }) })
    ] }) })
  ] });
}
function ModulePill({ Icon, label, className, anim }) {
  return /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: cn("absolute hidden lg:block", className), children: /* @__PURE__ */ jsx("div", { className: anim, children: /* @__PURE__ */ jsxs("div", { className: "glass-dark flex items-center gap-3 rounded-2xl py-3 pl-3 pr-5 shadow-[0_24px_50px_-24px_rgba(0,0,0,0.6)]", children: [
    /* @__PURE__ */ jsx("span", { className: "grid size-9 place-items-center rounded-xl bg-forest-300/20 text-forest-200", children: /* @__PURE__ */ jsx(Icon, { className: "size-[18px]", strokeWidth: 1.8 }) }),
    /* @__PURE__ */ jsx("p", { className: "font-display text-sm font-bold text-white", children: label })
  ] }) }) });
}
function FinalCTA() {
  return /* @__PURE__ */ jsx("section", { id: "inscription", "aria-labelledby": "cta-title", className: "relative px-3 pb-10 pt-6 sm:px-5 sm:pb-16 lg:pb-24", children: /* @__PURE__ */ jsx(Container, { className: "max-w-7xl px-0 sm:px-0", children: /* @__PURE__ */ jsx(Reveal, { variant: "scale", children: /* @__PURE__ */ jsxs("div", { className: "grain relative isolate overflow-hidden rounded-[2rem] bg-forest-950 px-6 py-16 text-center text-white shadow-[0_60px_100px_-50px_rgba(13,26,19,0.9)] sm:rounded-[2.75rem] sm:px-12 sm:py-24 lg:py-28", children: [
    /* @__PURE__ */ jsxs("div", { "aria-hidden": "true", className: "pointer-events-none absolute inset-0 -z-10", children: [
      /* @__PURE__ */ jsx("div", { className: "bg-grid-dark absolute inset-0" }),
      /* @__PURE__ */ jsx("div", { className: "absolute left-1/2 top-[-30%] size-[44rem] -translate-x-1/2 animate-drift rounded-full bg-forest-500/35 blur-[120px]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -bottom-40 -left-20 size-[26rem] animate-drift rounded-full bg-gold-500/15 blur-[110px] [animation-delay:-7s]" }),
      /* @__PURE__ */ jsx("div", { className: "absolute -bottom-40 -right-20 size-[26rem] animate-drift rounded-full bg-forest-400/25 blur-[110px] [animation-delay:-3s]" })
    ] }),
    /* @__PURE__ */ jsx(ModulePill, { Icon: ShoppingBag, label: "Ventes", className: "left-10 top-14 xl:left-20", anim: "animate-float" }),
    /* @__PURE__ */ jsx(ModulePill, { Icon: Package, label: "Inventaire", className: "bottom-16 left-16 xl:left-32", anim: "animate-float-slow [animation-delay:-4s]" }),
    /* @__PURE__ */ jsx(ModulePill, { Icon: FileText, label: "Factures", className: "right-10 top-24 xl:right-24", anim: "animate-float-slow [animation-delay:-2s]" }),
    /* @__PURE__ */ jsx(ModulePill, { Icon: Wallet, label: "Créances", className: "bottom-12 right-14 xl:right-28", anim: "animate-float [animation-delay:-5s]" }),
    /* @__PURE__ */ jsxs("div", { className: "relative mx-auto max-w-3xl", children: [
      /* @__PURE__ */ jsx(Reveal, { children: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-4 py-1.5 text-xs font-semibold text-forest-200", children: [
        /* @__PURE__ */ jsxs("span", { className: "relative flex size-2", children: [
          /* @__PURE__ */ jsx("span", { className: "absolute inset-0 animate-pulse-ring rounded-full bg-forest-300" }),
          /* @__PURE__ */ jsx("span", { className: "relative size-2 rounded-full bg-forest-300" })
        ] }),
        "Création de compte gratuite"
      ] }) }),
      /* @__PURE__ */ jsx(Reveal, { delay: 100, children: /* @__PURE__ */ jsxs("h2", { id: "cta-title", className: "mt-6 text-balance font-display text-[2.5rem] font-extrabold leading-[1] tracking-[-0.03em] sm:text-6xl lg:text-7xl", children: [
        "Prêt à piloter votre activité ",
        /* @__PURE__ */ jsx("span", { className: "text-gradient-light", children: "en toute simplicité ?" })
      ] }) }),
      /* @__PURE__ */ jsx(Reveal, { delay: 200, children: /* @__PURE__ */ jsx("p", { className: "mx-auto mt-6 max-w-xl text-pretty text-base leading-relaxed text-forest-200/80 sm:text-lg", children: "Réunissez vos ventes, vos boutiques, votre inventaire, vos clients et vos finances dans un seul espace." }) }),
      /* @__PURE__ */ jsxs(Reveal, { delay: 300, className: "mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row", children: [
        /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, variant: "light", size: "lg", arrow: true, className: "w-full sm:w-auto", children: "Créer un compte gratuitement" }),
        /* @__PURE__ */ jsx(Button, { href: LOGIN_URL, variant: "outlineLight", size: "lg", className: "w-full sm:w-auto", children: "Connexion" })
      ] }),
      /* @__PURE__ */ jsx(Reveal, { delay: 400, children: /* @__PURE__ */ jsx("ul", { className: "mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-forest-200/75", children: ["Interface en français", "Montants en FCFA", "Vue d'ensemble dès l'ouverture"].map((t) => /* @__PURE__ */ jsxs("li", { className: "inline-flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Check, { className: "size-4 text-forest-300", strokeWidth: 3, "aria-hidden": "true" }),
        t
      ] }, t)) }) })
    ] })
  ] }) }) }) });
}
const columns = [
  {
    title: "Produit",
    links: [
      { label: "Fonctionnalités", href: "#fonctionnalites" },
      { label: "Solutions", href: "#solutions" },
      { label: "Accompagnement", href: "#accompagnement" }
    ]
  },
  {
    title: "Ressources",
    links: [
      { label: "Questions fréquentes", href: "#faq" },
      { label: "Créer un compte", href: SIGNUP_URL },
      { label: "Connexion", href: LOGIN_URL }
    ]
  },
  {
    title: "Légal",
    links: [
      { label: "Mentions légales", href: void 0 },
      { label: "Confidentialité", href: void 0 },
      { label: "Conditions d'utilisation", href: void 0 }
    ]
  }
];
function Footer() {
  return /* @__PURE__ */ jsxs("footer", { className: "relative overflow-hidden border-t border-forest-900/[0.08] bg-paper-2/60 pb-28 pt-16 md:pb-10 lg:pt-20", children: [
    /* @__PURE__ */ jsxs(Container, { children: [
      /* @__PURE__ */ jsxs("div", { className: "grid gap-12 lg:grid-cols-[1.3fr_2fr]", children: [
        /* @__PURE__ */ jsxs("div", { className: "max-w-sm", children: [
          /* @__PURE__ */ jsx(Logo, {}),
          /* @__PURE__ */ jsx("p", { className: "mt-5 text-[15px] leading-relaxed text-ink-soft", children: "Gérez votre activité. Vendez partout. Ventes, boutiques, inventaire, clients, factures, comptabilité et créances : un espace simple, pensé pour les entrepreneurs africains." }),
          /* @__PURE__ */ jsxs(
            "a",
            {
              href: `mailto:${CONTACT_EMAIL}`,
              className: "group mt-6 inline-flex items-center gap-2.5 text-sm font-semibold text-ink transition-colors hover:text-forest-700",
              children: [
                /* @__PURE__ */ jsx("span", { className: "grid size-9 place-items-center rounded-xl border border-forest-900/10 bg-white transition-all duration-300 group-hover:-translate-y-0.5 group-hover:border-forest-600/40 group-hover:text-forest-700", children: /* @__PURE__ */ jsx(Mail, { className: "size-4", "aria-hidden": "true" }) }),
                CONTACT_EMAIL
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx("nav", { "aria-label": "Pied de page", className: "grid grid-cols-2 gap-8 sm:grid-cols-3", children: columns.map((c) => /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("h2", { className: "text-xs font-bold uppercase tracking-[0.18em] text-muted", children: c.title }),
          /* @__PURE__ */ jsx("ul", { className: "mt-5 space-y-3", children: c.links.map((l) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
            "a",
            {
              href: l.href,
              "aria-disabled": !l.href || void 0,
              className: "group relative inline-block text-[15px] font-medium text-ink/80 transition-colors hover:text-forest-700",
              children: [
                l.label,
                /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-forest-600 transition-transform duration-500 ease-out-expo group-hover:scale-x-100" })
              ]
            }
          ) }, l.label)) })
        ] }, c.title)) })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "mt-14 flex flex-col-reverse items-start justify-between gap-5 border-t border-forest-900/[0.08] pt-8 sm:flex-row sm:items-center", children: [
        /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted", children: [
          "© ",
          (/* @__PURE__ */ new Date()).getFullYear(),
          " NSARA. Tous droits réservés."
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm text-muted", children: "Fait avec soin pour les entrepreneurs africains" }),
          /* @__PURE__ */ jsx(
            "a",
            {
              href: "#top",
              "aria-label": "Retour en haut de page",
              className: "group grid size-10 place-items-center rounded-xl border border-forest-900/10 bg-white text-ink transition-all duration-300 hover:-translate-y-1 hover:border-forest-600/40 hover:bg-forest-700 hover:text-white",
              children: /* @__PURE__ */ jsx(ArrowUp, { className: "size-4 transition-transform duration-300 group-hover:-translate-y-0.5", "aria-hidden": "true" })
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { "aria-hidden": "true", className: "pointer-events-none mt-6 select-none text-center font-display text-[26vw] font-extrabold leading-[0.75] tracking-[-0.05em] text-forest-900/[0.045] sm:text-[20vw] lg:text-[16vw]", children: "NSARA" })
  ] });
}
function MobileCTA() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const cta = document.getElementById("inscription");
      const r2 = cta?.getBoundingClientRect();
      const ctaVisible = r2 ? r2.top < window.innerHeight * 0.85 && r2.bottom > 0 : false;
      setShow(window.scrollY > 700 && !ctaVisible);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return /* @__PURE__ */ jsx(
    "div",
    {
      inert: !show ? "" : void 0,
      style: { bottom: "calc(0.75rem + env(safe-area-inset-bottom))" },
      className: cn(
        "fixed inset-x-3 z-40 transition-all duration-500 ease-out-expo md:hidden",
        show ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "glass flex items-center gap-3 rounded-2xl p-2 pl-4 shadow-[0_20px_50px_-15px_rgba(19,33,26,0.5)]", children: [
        /* @__PURE__ */ jsxs("p", { className: "flex-1 text-[13px] font-semibold leading-tight text-ink", children: [
          "Compte gratuit",
          /* @__PURE__ */ jsx("span", { className: "block text-xs font-medium text-muted", children: "Interface en français" })
        ] }),
        /* @__PURE__ */ jsx(Button, { href: SIGNUP_URL, arrow: true, children: "Commencer" })
      ] })
    }
  );
}
function App() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "a",
      {
        href: "#contenu",
        className: "sr-only fixed left-4 top-4 z-[100] rounded-xl bg-forest-900 px-4 py-3 text-sm font-semibold text-white focus:not-sr-only",
        children: "Aller au contenu principal"
      }
    ),
    /* @__PURE__ */ jsx(Navbar, {}),
    /* @__PURE__ */ jsxs("main", { id: "contenu", children: [
      /* @__PURE__ */ jsx(Hero, {}),
      /* @__PURE__ */ jsx(SocialProof, {}),
      /* @__PURE__ */ jsx(Features, {}),
      /* @__PURE__ */ jsx(Showcase, {}),
      /* @__PURE__ */ jsx(Benefits, {}),
      /* @__PURE__ */ jsx(Support, {}),
      /* @__PURE__ */ jsx(FAQ, {}),
      /* @__PURE__ */ jsx(FinalCTA, {})
    ] }),
    /* @__PURE__ */ jsx(Footer, {}),
    /* @__PURE__ */ jsx(MobileCTA, {})
  ] });
}
function PremiumLanding({ authenticated = false }) {
  const root = useRef(null);
  useEffect(() => {
    if (!authenticated || !root.current) return;
    root.current.querySelectorAll('a[href="/register"], a[href="/login"]').forEach((link) => {
      link.href = "/dashboard";
      const label = [...link.childNodes].some((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()) ? link : link.querySelector("span") || link;
      const text = [...label.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
      if (text) text.textContent = "Accéder à mon espace";
      const prompt = link.parentElement;
      if (prompt?.tagName === "P") {
        [...prompt.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.includes("Déjà un compte")).forEach((node) => { node.textContent = ""; });
      }
    });
  }, [authenticated]);
  return /* @__PURE__ */ jsx("div", { ref: root, children: /* @__PURE__ */ jsx(App, {}) });
}
export {
  PremiumLanding as default
};
